import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, Calendar, Check, ChevronRight, Clock, CreditCard, Download, Edit, FileText, Heart, Info,
  Mail, MapPin, Package, Phone, Plus, RefreshCw, RotateCcw, Search, Settings, Sparkles, Trash2, Upload, Users, X,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

import { AdminEventManager } from "../components/admin/AdminEventManager";
import { AdminRetreatsManager } from "../components/admin/AdminRetreatsManager";
import { AdminBlogManager } from "../components/admin/AdminBlogManager";
import { AdminPromoManager } from "../components/admin/AdminPromoManager";
import { AdminProductsManager } from "../components/admin/AdminProductsManager";
import { AdminPushBroadcast } from "../components/admin/AdminPushBroadcast";
import { AdminMainCategoriesManager } from "../components/admin/AdminMainCategoriesManager";
import { AdminNewsletterManager } from "../components/admin/AdminNewsletterManager";
import { ClientPicker } from "../components/admin/ClientPicker";
import { SquareSyncButton } from "../components/admin/SquareSyncButton";
import PayPendingModal from "../components/PayPendingModal";
import RescheduleModal from "../components/RescheduleModal";
import { PushSubscribeButton } from "../components/PushSubscribeButton";
import { ProfileSecuritySection } from "../components/AuthPages";

import { useAuth } from "../contexts/AppContexts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helpers locaux (dupliqués d'App.js pour éviter une dépendance circulaire).
function formatHour(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  return m === '00' ? `${parseInt(h)}h` : `${parseInt(h)}h${m}`;
}
function formatLocalDate(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DAY_LABELS = { 0: 'Lundi', 1: 'Mardi', 2: 'Mercredi', 3: 'Jeudi', 4: 'Vendredi', 5: 'Samedi', 6: 'Dimanche' };

function ImageUploader({ currentUrl, onUploaded, category = "uploads" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      const res = await axios.post(`${API}/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.url) {
        onUploaded(res.data.url);
        toast.success("Image uploadée");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid="image-uploader">
      {currentUrl && (
        <div className="w-full h-32 rounded-lg overflow-hidden border border-white/10 bg-[#1B132B]">
          <img src={currentUrl.startsWith('/') ? `${BACKEND_URL}${currentUrl}` : currentUrl} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
      <div className="flex gap-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        <Button type="button" variant="outline" size="sm" className="border-white/10 text-[#B59ED1] hover:text-white flex-1" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn">
          {uploading ? 'Upload...' : currentUrl ? 'Changer la photo' : 'Ajouter une photo'}
        </Button>
        {currentUrl && (
          <Button type="button" variant="ghost" size="sm" className="text-red-400" onClick={() => onUploaded('')} data-testid="remove-img-btn">
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

function AdminServiceManager() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', short_description: '', duration_minutes: 60,
    price: 0, image_url: '', category: 'Voyance', has_variations: false,
    variations: [], location_modes: ['presentiel']
  });

  const CATEGORIES = ['Voyance', 'Soin Énergétique', 'Purification', 'Numérologie'];

  const refreshServices = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refreshServices(); }, []);

  const openNew = () => {
    setEditing('new');
    setForm({ name: '', description: '', short_description: '', duration_minutes: 60, price: 0, image_url: '', category: 'Voyance', has_variations: false, variations: [], location_modes: ['presentiel'] });
  };

  const openEdit = (s) => {
    setEditing(s.service_id);
    setForm({
      name: s.name, description: s.description, short_description: s.short_description || '',
      duration_minutes: s.duration_minutes, price: s.price, image_url: s.image_url || '',
      category: s.category, has_variations: s.has_variations || false,
      variations: s.variations || [], location_modes: s.location_modes || ['presentiel']
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.description) { toast.error("Nom et description requis"); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/services/create`, form, { withCredentials: true });
        toast.success("Service créé !");
      } else {
        await axios.put(`${API}/services/${editing}`, form, { withCredentials: true });
        toast.success("Service mis à jour !");
      }
      setEditing(null);
      refreshServices();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (serviceId) => {
    try {
      await axios.delete(`${API}/services/${serviceId}`, { withCredentials: true });
      toast.success("Service supprimé");
      refreshServices();
    } catch (error) { toast.error("Erreur"); }
  };

  const addVariation = () => {
    setForm({...form, variations: [...form.variations, { variation_id: `var_${Date.now()}`, name: '', price: 0 }]});
  };
  const removeVariation = (idx) => {
    setForm({...form, variations: form.variations.filter((_, i) => i !== idx)});
  };
  const updateVariation = (idx, field, value) => {
    const updated = [...form.variations];
    updated[idx] = {...updated[idx], [field]: field === 'price' ? parseFloat(value) || 0 : value};
    setForm({...form, variations: updated});
  };

  const toggleLocationMode = (mode) => {
    const modes = form.location_modes.includes(mode)
      ? form.location_modes.filter(m => m !== mode)
      : [...form.location_modes, mode];
    if (modes.length === 0) return;
    setForm({...form, location_modes: modes});
  };

  return (
    <Card className="bg-[#261B3B] border-white/10 mt-8" data-testid="admin-service-manager">
      <CardHeader>
        <CardTitle className="font-serif text-xl flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#D926A9]" />
          Gestion des Services
        </CardTitle>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="p-5 bg-[#1B132B] rounded-lg space-y-4" data-testid="service-form">
            <div className="flex items-center justify-between">
              <h3 className="text-sm uppercase tracking-wider text-[#B59ED1]">{editing === 'new' ? 'Nouveau service' : 'Modifier le service'}</h3>
              <button onClick={() => setEditing(null)} className="text-[#B59ED1] hover:text-white text-sm">Annuler</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Nom</label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#261B3B] border-white/10" data-testid="svc-name" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Catégorie</label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="bg-[#261B3B] border-white/10" data-testid="svc-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Accroche courte</label>
              <Input value={form.short_description} onChange={(e) => setForm({...form, short_description: e.target.value})} className="bg-[#261B3B] border-white/10" data-testid="svc-short-desc" />
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-1 block">Description complète</label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-[#261B3B] border-white/10 min-h-[120px]" data-testid="svc-description" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Prix (€)</label>
                <Input type="number" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})} className="bg-[#261B3B] border-white/10" data-testid="svc-price" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Durée (min)</label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value) || 0})} className="bg-[#261B3B] border-white/10" data-testid="svc-duration" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Photo</label>
                <ImageUploader currentUrl={form.image_url} onUploaded={(url) => setForm({...form, image_url: url})} category="services" />
              </div>
            </div>

            {/* Location modes */}
            <div>
              <label className="text-xs text-[#B59ED1] mb-2 block">Mode de consultation</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => toggleLocationMode('presentiel')}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${form.location_modes.includes('presentiel') ? 'bg-[#D926A9]/20 text-[#D926A9] border border-[#D926A9]/40' : 'bg-[#261B3B] text-[#B59ED1] border border-white/10'}`}
                  data-testid="svc-mode-presentiel"
                ><MapPin className="w-3 h-3 inline mr-1" />En boutique</button>
                <button type="button" onClick={() => toggleLocationMode('distance')}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${form.location_modes.includes('distance') ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/40' : 'bg-[#261B3B] text-[#B59ED1] border border-white/10'}`}
                  data-testid="svc-mode-distance"
                ><Phone className="w-3 h-3 inline mr-1" />À distance</button>
              </div>
            </div>

            {/* Variations */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-[#B59ED1]">Formules / Variations</label>
                <Button type="button" variant="ghost" size="sm" className="text-[#00E5FF] text-xs" onClick={addVariation} data-testid="add-variation">
                  <Plus className="w-3 h-3 mr-1" />Ajouter
                </Button>
              </div>
              {form.variations.length > 0 && (
                <div className="space-y-2">
                  {form.variations.map((v, idx) => (
                    <div key={v.variation_id || `var-${idx}`} className="flex gap-2 items-center">
                      <Input value={v.name} onChange={(e) => updateVariation(idx, 'name', e.target.value)} placeholder="Nom de la formule" className="bg-[#261B3B] border-white/10 flex-1 text-sm" />
                      <Input type="number" value={v.price} onChange={(e) => updateVariation(idx, 'price', e.target.value)} className="bg-[#261B3B] border-white/10 w-24 text-sm" />
                      <span className="text-xs text-[#B59ED1]">€</span>
                      <Button type="button" variant="ghost" size="sm" className="text-red-400 p-1" onClick={() => removeVariation(idx)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              {form.variations.length > 0 && (
                <label className="flex items-center gap-2 mt-2 text-xs text-[#B59ED1]">
                  <input type="checkbox" checked={form.has_variations} onChange={(e) => setForm({...form, has_variations: e.target.checked})} className="rounded" />
                  Afficher le sélecteur de formules
                </label>
              )}
            </div>

            <Button onClick={handleSave} disabled={saving} className="gradient-primary w-full" data-testid="save-service-btn">
              {saving ? 'Enregistrement...' : editing === 'new' ? 'Créer le service' : 'Enregistrer les modifications'}
            </Button>
          </div>
        ) : (
          <>
            <Button onClick={openNew} className="gradient-primary mb-4" data-testid="new-service-btn">
              <Plus className="w-4 h-4 mr-2" />Nouveau service
            </Button>
            <div className="space-y-3">
              {services.map(s => (
                <div key={s.service_id} className="p-4 bg-[#1B132B] rounded-lg flex items-start gap-4" data-testid={`svc-row-${s.service_id}`}>
                  {s.image_url && (
                    <img src={s.image_url.startsWith('/') ? `${BACKEND_URL}${s.image_url}` : s.image_url} alt={s.name} className="w-20 h-14 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-medium truncate">{s.name}</h4>
                      <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">{s.category}</Badge>
                      {s.location_modes?.map(m => (
                        <Badge key={m} className={`text-xs ${m === 'presentiel' ? 'bg-[#D926A9]/10 text-[#D926A9]' : 'bg-[#00E5FF]/10 text-[#00E5FF]'}`}>
                          {m === 'presentiel' ? 'Boutique' : 'Distance'}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-[#B59ED1] text-sm truncate">{s.short_description}</p>
                    <div className="flex gap-3 mt-1 text-xs text-[#B59ED1]">
                      <span>{s.price}€</span>
                      <span>{s.duration_minutes} min</span>
                      {s.has_variations && <span>{s.variations?.length} formules</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="text-[#00E5FF]" onClick={() => openEdit(s)} data-testid={`edit-svc-${s.service_id}`}>
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete(s.service_id)} data-testid={`delete-svc-${s.service_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}




export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminTab, setAdminTab] = useState(() => {
    // Deeplink support via ?tab=... (utilisé par les push notifs admin)
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get('tab');
      const allowed = ['calendar', 'appointments', 'availability', 'orders', 'services', 'products', 'events', 'blog', 'promo', 'shop-cats', 'clients', 'notifications', 'newsletter', 'stats', 'imports', 'exports'];
      if (t && allowed.includes(t)) return t;
    } catch (_) { /* ignore */ }
    return 'calendar';
  });
  // Auto-scroll en haut à chaque changement d'onglet admin (les vues changent sans
  // changement de route → le ScrollToTop global ne s'applique pas). On saute le 1er rendu.
  const adminTabFirstRender = useRef(true);
  useEffect(() => {
    if (adminTabFirstRender.current) { adminTabFirstRender.current = false; return; }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [adminTab]);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [calendarView, setCalendarView] = useState('week');
  const [calendarZoom, setCalendarZoom] = useState('compact'); // compact | normal | expanded
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null); // appointmentId en attente de confirmation d'annulation
  // Modale "Payer maintenant" pour RDV / commandes / événements en attente
  const [payModal, setPayModal] = useState(null);
  // Modale de details d'un RDV (resume client + boutons d'action)
  const [aptDetailModal, setAptDetailModal] = useState(null);
  const [eventDetailModal, setEventDetailModal] = useState(null);
  // Modale de creation de RDV cote admin
  const [newAptModal, setNewAptModal] = useState(null); // { date, time } prefill optionnel
  // Modale d'expedition (numero de suivi Mondial Relay)
  const [shipModal, setShipModal] = useState(null); // { order }
  const [shipTracking, setShipTracking] = useState('');
  const [shipUrl, setShipUrl] = useState('');
  const [newApt, setNewApt] = useState({
    service_id: '', variation_id: '', location_mode: 'presentiel',
    date: '', time_slot: '', customer_name: '', customer_email: '', customer_phone: '',
    notes: '', payment_method: 'pay_at_shop'
  });
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockDate, setBlockDate] = useState('');
  const [blockDateTo, setBlockDateTo] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockAllDay, setBlockAllDay] = useState(true);
  const [blockTimeFrom, setBlockTimeFrom] = useState('09:00');
  const [blockTimeTo, setBlockTimeTo] = useState('11:00');
  const [recurringBlocks, setRecurringBlocks] = useState([]);
  const [recDay, setRecDay] = useState('');
  const [recAllDay, setRecAllDay] = useState(true);
  const [recTimeFrom, setRecTimeFrom] = useState('10:00');
  const [recTimeTo, setRecTimeTo] = useState('12:00');
  const [recReason, setRecReason] = useState('');
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const CLIENTS_PER_PAGE = 20;
  const [clientDetail, setClientDetail] = useState(null);
  const [clientDetailLoading, setClientDetailLoading] = useState(false);
  const [clientNotes, setClientNotes] = useState('');
  // Édition inline identité/téléphone (admin)
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [clientEditForm, setClientEditForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [clientEditSaving, setClientEditSaving] = useState(false);

  // --- Wix CSV Import (admin) ---
  const [wixImportOpen, setWixImportOpen] = useState(false);
  const [wixFile, setWixFile] = useState(null);
  const [wixPushBrevo, setWixPushBrevo] = useState(true);
  const [wixImporting, setWixImporting] = useState(false);
  const [wixReport, setWixReport] = useState(null);

  // --- CSV exports modal (admin backups) ---
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportingKind, setExportingKind] = useState(null);

  const handleExport = async (kind) => {
    setExportingKind(kind);
    try {
      const res = await axios.get(`${API}/admin/export/${kind}.csv`, {
        responseType: 'blob',
        withCredentials: true,
      });
      // Extract filename from Content-Disposition header (fallback to default)
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `bilan2vie-${kind}.csv`;
      // Trigger browser download
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Export "${kind}" téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Export ${kind} échoué`);
    } finally {
      setExportingKind(null);
    }
  };

  const handleWixImport = async () => {
    if (!wixFile) { toast.error("Sélectionne un fichier CSV"); return; }
    setWixImporting(true);
    setWixReport(null);
    try {
      const fd = new FormData();
      fd.append('file', wixFile);
      const res = await axios.post(
        `${API}/admin/contacts/import-wix?push_to_brevo=${wixPushBrevo ? 'true' : 'false'}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setWixReport(res.data.report);
      toast.success(`Import terminé : ${res.data.report.created} créés, ${res.data.report.updated} enrichis`);
      // Refresh clients list in background
      try {
        const listRes = await axios.get(`${API}/clients`, { withCredentials: true });
        setClients(listRes.data || []);
      } catch (refreshErr) {
        console.warn("Refresh clients after Wix import failed (non-blocking):", refreshErr);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import échoué");
    } finally {
      setWixImporting(false);
    }
  };

  const openClientDetail = async (userId) => {
    setClientDetailLoading(true);
    setClientDetail({ loading: true, user_id: userId });
    try {
      const res = await axios.get(`${API}/clients/${userId}`, { withCredentials: true });
      setClientDetail(res.data);
      setClientNotes(res.data.user?.admin_notes || '');
    } catch (e) {
      toast.error("Erreur chargement fiche");
      setClientDetail(null);
    } finally { setClientDetailLoading(false); }
  };

  const saveClientNotes = async () => {
    if (!clientDetail?.user?.user_id) return;
    try {
      await axios.put(`${API}/clients/${clientDetail.user.user_id}/notes`, { admin_notes: clientNotes }, { withCredentials: true });
      toast.success("Note enregistrée");
      setClientDetail({ ...clientDetail, user: { ...clientDetail.user, admin_notes: clientNotes } });
    } catch (e) { toast.error("Erreur"); }
  };

  const openClientEdit = () => {
    const u = clientDetail?.user;
    if (!u) return;
    setClientEditForm({
      first_name: u.first_name || (u.name ? String(u.name).split(' ')[0] : '') || '',
      last_name: u.last_name || (u.name ? String(u.name).split(' ').slice(1).join(' ') : '') || '',
      phone: u.phone || '',
    });
    setClientEditOpen(true);
  };

  const saveClientProfile = async () => {
    const u = clientDetail?.user;
    if (!u?.user_id) return;
    const payload = {
      first_name: clientEditForm.first_name.trim(),
      last_name: clientEditForm.last_name.trim(),
      phone: clientEditForm.phone.trim(),
    };
    if (!payload.first_name || !payload.last_name) {
      toast.error("Prénom et nom sont obligatoires");
      return;
    }
    setClientEditSaving(true);
    try {
      const res = await axios.put(`${API}/clients/${u.user_id}/profile`, payload, { withCredentials: true });
      const updatedUser = res.data?.user || { ...u, ...payload, name: `${payload.first_name} ${payload.last_name}`.trim() };
      setClientDetail({ ...clientDetail, user: updatedUser });
      // Reflète aussi dans la liste des clients (pour re-render immédiat)
      setClients(prev => prev.map(c => c.user_id === u.user_id ? { ...c, first_name: updatedUser.first_name, last_name: updatedUser.last_name, name: updatedUser.name, phone: updatedUser.phone } : c));
      toast.success("Client mis à jour");
      setClientEditOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setClientEditSaving(false);
    }
  };

  const deleteClient = async () => {
    const u = clientDetail?.user;
    if (!u?.user_id) return;
    const confirmMsg = `Supprimer définitivement le compte de ${u.name || u.email} ?\n\n` +
      `• Le client ne pourra plus se connecter\n` +
      `• Il sera retiré de la newsletter (Brevo)\n` +
      `• L'historique des commandes et RDV est conservé pour ta comptabilité\n\n` +
      `Cette action est irréversible.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/clients/${u.user_id}`, { withCredentials: true });
      toast.success(`Compte supprimé : ${u.email}`);
      setClientDetail(null);
      setClients(prev => prev.filter(c => c.user_id !== u.user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };
  const [services, setServices] = useState([]);
  const [editShopHours, setEditShopHours] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const fetchData = async () => {
    try {
      const params = {};
      if (dateFilter) { params.date_from = dateFilter; params.date_to = dateFilter; }
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (serviceFilter && serviceFilter !== 'all') params.service_id = serviceFilter;
      const [appointmentsRes, ordersRes, eventsRes] = await Promise.all([
        axios.get(`${API}/appointments`, { params, withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/events?upcoming_only=true`),
      ]);
      setAppointments(appointmentsRes.data);
      setOrders(ordersRes.data);
      setEvents(eventsRes.data || []);
      if (user.role === 'admin') {
        const [statsRes, blockedRes, svcRes, settingsRes, recurRes, eventsAllRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/availability/blocked`, { withCredentials: true }),
          axios.get(`${API}/services`),
          axios.get(`${API}/availability/settings`),
          axios.get(`${API}/availability/recurring`),
          axios.get(`${API}/events?upcoming_only=true`)
        ]);
        setStats(statsRes.data);
        setBlockedSlots(blockedRes.data);
        setServices(svcRes.data);
        if (!editShopHours) setEditShopHours(settingsRes.data.shop_hours || {});
        setRecurringBlocks(recurRes.data);
        setEvents(eventsAllRes.data || []);
      }
    } catch (error) { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Attendre que l'authentification soit resolue avant de rediriger
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    fetchData();
    // fetchData lit les filtres dans le closure courant — re-trigger via les filtres listes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate, dateFilter, statusFilter, serviceFilter]);

  const cancelAppointment = (appointmentId) => setCancelConfirm(appointmentId);

  const doCancelAppointment = async () => {
    const appointmentId = cancelConfirm;
    if (!appointmentId) return;
    try {
      await axios.put(`${API}/appointments/${appointmentId}/status`, { status: 'cancelled' }, { withCredentials: true });
      setAppointments(prev => prev.map(a => a.appointment_id === appointmentId ? { ...a, status: 'cancelled' } : a));
      toast.success("Rendez-vous annulé");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setCancelConfirm(null); }
  };

  const completeAppointment = async (appointmentId) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/status`, { status: 'completed' }, { withCredentials: true });
      setAppointments(prev => prev.map(a => a.appointment_id === appointmentId ? { ...a, status: 'completed' } : a));
      toast.success("Rendez-vous marqué terminé");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const markAppointmentPaid = async (appointmentId) => {
    if (!window.confirm("Marquer ce rendez-vous comme payé (encaissement manuel) ? Le client recevra un email de confirmation si le RDV était en attente de paiement.")) return;
    try {
      await axios.put(`${API}/appointments/${appointmentId}/mark-paid`, { method: 'manual' }, { withCredentials: true });
      setAppointments(prev => prev.map(a => a.appointment_id === appointmentId
        ? { ...a, payment_status: 'paid', status: a.status === 'pending_payment' ? 'confirmed' : a.status }
        : a));
      toast.success("Rendez-vous marqué comme payé");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) { toast.error("Date et heure requises"); return; }
    try {
      await axios.put(`${API}/appointments/${rescheduleModal}/reschedule`, { date: rescheduleDate, time_slot: rescheduleTime }, { withCredentials: true });
      toast.success("Rendez-vous reprogrammé");
      setRescheduleModal(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  // Ouvre la modale de creation admin avec un prefill (date/heure cliquee dans le calendrier)
  const openNewApt = (prefill = {}) => {
    setNewApt({
      service_id: '', variation_id: '', location_mode: 'presentiel',
      date: prefill.date || formatLocalDate(new Date()),
      time_slot: prefill.time_slot || '',
      customer_name: '', customer_email: '', customer_phone: '',
      notes: '', payment_method: 'pay_at_shop'
    });
    setNewAptAvailableSlots([]);
    setNewAptModal(true);
  };

  // Creneaux intelligents pour la creation admin
  const [newAptAvailableSlots, setNewAptAvailableSlots] = useState([]);
  const [newAptLoadingSlots, setNewAptLoadingSlots] = useState(false);

  useEffect(() => {
    if (!newApt.service_id || !newApt.date) { setNewAptAvailableSlots([]); return; }
    setNewAptLoadingSlots(true);
    const params = { date: newApt.date, service_id: newApt.service_id, admin: true };
    if (newApt.variation_id) params.variation_id = newApt.variation_id;
    axios.get(`${API}/appointments/available-slots`, { params, withCredentials: true })
      .then(r => setNewAptAvailableSlots(r.data.available_slots || []))
      .catch(() => setNewAptAvailableSlots([]))
      .finally(() => setNewAptLoadingSlots(false));
  }, [newApt.service_id, newApt.variation_id, newApt.date]);

  // Met a jour le statut d'une commande + declenche l'email si necessaire
  const updateOrderStatus = async (orderId, newStatus, extras = {}) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus, ...extras }, { withCredentials: true });
      toast.success(newStatus === 'shipped' ? 'Commande marquée expédiée et email envoyé' : 'Statut mis à jour');
      fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
      return false;
    }
  };

  const handleShipOrder = async () => {
    if (!shipTracking.trim()) { toast.error("Le numéro de suivi est requis"); return; }
    const ok = await updateOrderStatus(shipModal.order.order_id, 'shipped', {
      tracking_number: shipTracking.trim(),
      tracking_url: shipUrl.trim() || undefined,
    });
    if (ok) {
      setShipModal(null);
      setShipTracking('');
      setShipUrl('');
    }
  };

  const handleCreateAdminApt = async () => {
    if (!newApt.service_id) { toast.error("Choisis un service"); return; }
    if (!newApt.date || !newApt.time_slot) { toast.error("Date et heure requises"); return; }
    if (!newApt.customer_name) { toast.error("Nom du client requis"); return; }
    try {
      const payload = {
        ...newApt,
        variation_id: newApt.variation_id || null,
      };
      await axios.post(`${API}/admin/appointments`, payload, { withCredentials: true });
      toast.success("Rendez-vous créé");
      setNewAptModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur à la création");
    }
  };

  const handleBlockDay = async () => {
    if (!blockDate) { toast.error("Sélectionnez une date de début"); return; }
    if (!blockAllDay) {
      if (!blockTimeFrom || !blockTimeTo) { toast.error("Indique une plage horaire complète"); return; }
      if (blockTimeFrom >= blockTimeTo) { toast.error("L'heure de fin doit être après l'heure de début"); return; }
    }
    try {
      const payload = {
        date_from: blockDate,
        date_to: blockDateTo || blockDate,
        all_day: blockAllDay,
        reason: blockReason
      };
      if (!blockAllDay) {
        payload.time_from = blockTimeFrom;
        payload.time_to = blockTimeTo;
      }
      await axios.post(`${API}/availability/block`, payload, { withCredentials: true });
      toast.success(
        blockAllDay
          ? (blockDateTo && blockDateTo !== blockDate ? "Plage de jours bloquée" : "Jour bloqué")
          : `Créneau ${blockTimeFrom}–${blockTimeTo} bloqué`
      );
      setBlockDate(''); setBlockDateTo(''); setBlockReason('');
      const res = await axios.get(`${API}/availability/blocked`, { withCredentials: true });
      setBlockedSlots(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const handleUnblock = async (blockId) => {
    try {
      await axios.delete(`${API}/availability/block/${blockId}`, { withCredentials: true });
      setBlockedSlots(prev => prev.filter(b => b.block_id !== blockId));
      toast.success("Jour débloqué");
    } catch (error) { toast.error("Erreur"); }
  };

  const updateShopHour = (day, field, value) => {
    setEditShopHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const addShopDay = (day) => {
    setEditShopHours(prev => ({ ...prev, [day]: { open: '10:00', close: '19:00' } }));
  };

  const removeShopDay = (day) => {
    setEditShopHours(prev => { const n = { ...prev }; delete n[day]; return n; });
  };

  const saveShopHours = async () => {
    setSavingHours(true);
    try {
      const settingsRes = await axios.get(`${API}/availability/settings`);
      const current = settingsRes.data;
      await axios.put(`${API}/availability/settings`, {
        ...current,
        shop_hours: editShopHours
      }, { withCredentials: true });
      toast.success("Horaires boutique mis à jour !");
    } catch (error) { toast.error("Erreur"); }
    finally { setSavingHours(false); }
  };

  const handleAddRecurring = async () => {
    if (recDay === '') { toast.error("Sélectionnez un jour"); return; }
    try {
      const body = {
        day_of_week: parseInt(recDay),
        all_day: recAllDay,
        reason: recReason
      };
      if (!recAllDay) {
        body.time_from = recTimeFrom;
        body.time_to = recTimeTo;
      }
      await axios.post(`${API}/availability/recurring`, body, { withCredentials: true });
      toast.success("Blocage récurrent ajouté");
      setRecDay(''); setRecReason(''); setRecAllDay(true);
      const res = await axios.get(`${API}/availability/recurring`);
      setRecurringBlocks(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const handleDeleteRecurring = async (recurringId) => {
    try {
      await axios.delete(`${API}/availability/recurring/${recurringId}`, { withCredentials: true });
      setRecurringBlocks(prev => prev.filter(r => r.recurring_id !== recurringId));
      toast.success("Blocage récurrent supprimé");
    } catch (error) { toast.error("Erreur"); }
  };

  // Note : l'envoi des rappels 24h est entièrement automatique (cron backend tous les jours à 9h00 Paris).
  // Plus de bouton manuel — voir scheduled_reminders dans server.py.

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (e) { console.error(e); }
  };

  // Calendar helpers
  const getWeekDays = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Dim, 1=Lun, ... 6=Sam
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Décalage pour aligner sur la grille Lun-Dim (lundi = colonne 0)
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const days = [];
    // Cellules vides avant le 1er du mois pour aligner les colonnes correctement
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  };

  const formatDateKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const getAptsForDate = (dateStr) => appointments
    .filter(a => a.date === dateStr && a.status !== 'cancelled')
    // Côté admin : on masque du calendrier les RDV client non payés tant qu'ils ne sont
    // pas réglés (ils ne tiennent le créneau que 10 min puis sont supprimés). Les RDV
    // créés par l'admin (lien de paiement) restent visibles.
    .filter(a => !(user?.role === 'admin' && a.status === 'pending_payment' && a.payment_status !== 'paid' && !a.created_by_admin))
    .sort((x, y) => (x.time_slot || '').localeCompare(y.time_slot || ''));

  const getEventsForDate = (dateStr) => events
    .filter(e => e.date === dateStr && e.status !== 'cancelled')
    .sort((x, y) => (x.time || '').localeCompare(y.time || ''));

  const DAY_NAMES_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const MONTH_NAMES = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  const navigateCalendar = (dir) => {
    const d = new Date(calendarDate);
    if (calendarView === 'day') d.setDate(d.getDate() + dir);
    else if (calendarView === 'week') d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setCalendarDate(d);
  };

  // Pendant le chargement de l'auth, afficher un spinner plutot que rediriger trop tot
  if (authLoading) {
    return (
      <main className="pt-32 min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center text-[#B59ED1]">
          <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#D926A9] animate-pulse" />
          <p className="text-sm">Chargement de ton espace...</p>
        </div>
      </main>
    );
  }
  if (!user) return null;
  const isAdmin = user.role === 'admin';

  const canModify = (apt) => {
    const aptDate = new Date(`${apt.date}T${apt.time_slot}:00Z`);
    return (aptDate - new Date()) > 3 * 60 * 60 * 1000 && apt.status !== 'cancelled' && apt.status !== 'completed';
  };

  // Admin peut toujours modifier/annuler un RDV, même après l'avoir marqué comme
  // "terminé" (utile en cas de clic accidentel). Seul le statut "cancelled"
  // (déjà annulé) reste terminal — inutile d'annuler 2 fois.
  const canAdminModify = (apt) => apt.status !== 'cancelled';

  // --- Blocages dans l'agenda ---
  // Jour entièrement bloqué : il existe un blocage all_day pour cette date.
  const isDayFullyBlocked = (dateStr) => blockedSlots.some(b => b.date === dateStr && b.all_day);
  // Calcule l'heure de fin par défaut (+60 min) pour les anciens blocages `time_slot`.
  const _plus60 = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const e = h * 60 + m + 60;
    return `${String(Math.floor(e / 60)).padStart(2, '0')}:${String(e % 60).padStart(2, '0')}`;
  };
  // Rend des bandes rouges UNIQUEMENT sur les créneaux partiels bloqués (pas toute la journée).
  const renderBlockBands = (dateStr, toPx) => {
    const partials = blockedSlots.filter(b => b.date === dateStr && !b.all_day);
    return partials.map((b, idx) => {
      const from = b.time_from || b.time_slot;
      if (!from) return null;
      const to = b.time_to || _plus60(from);
      const top = Math.max(toPx(from), 0);
      const height = Math.max(toPx(to) - top, 10);
      return (
        <div key={b.block_id || idx}
          className="absolute left-0.5 right-0.5 rounded bg-red-500/20 border border-red-400/40 pointer-events-none flex items-center justify-center overflow-hidden"
          style={{ top: top + 'px', height: height + 'px', zIndex: 1 }}
          title={`Créneau bloqué ${from}–${to}${b.reason ? ' · ' + b.reason : ''}`}
          data-testid={`cal-block-${dateStr}-${from}`}>
          <span className="text-[9px] text-red-300 font-medium px-1 truncate">Bloqué {from}–{to}</span>
        </div>
      );
    });
  };

  const restoreAppointment = async (appointmentId) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/status`, { status: 'confirmed' }, { withCredentials: true });
      setAppointments(prev => prev.map(a => a.appointment_id === appointmentId ? { ...a, status: 'confirmed' } : a));
      toast.success("Rendez-vous rétabli (Confirmé)");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const statusLabel = (s) => ({
    pending_payment: 'En attente paiement', confirmed: 'Confirmé', completed: 'Terminé',
    cancelled: 'Annulé', no_show: 'Absent'
  }[s] || s);

  const statusColor = (s) => ({
    pending_payment: 'bg-yellow-500/20 text-yellow-400', confirmed: 'bg-green-500/20 text-green-400',
    completed: 'bg-blue-500/20 text-blue-400', cancelled: 'bg-red-500/20 text-red-400', no_show: 'bg-orange-500/20 text-orange-400'
  }[s] || 'bg-white/10 text-white');

  const paymentLabel = (s) => ({
    paid: 'Payé en ligne', pay_at_shop: 'Paiement boutique', unpaid: 'Non payé', refunded: 'Remboursé'
  }[s] || s);

  // RDV a venir, tries du plus proche au plus eloigne (par date + heure)
  // On fusionne les RDV consultations + les inscriptions à des événements/ateliers
  // (côté client : ses propres inscriptions ; côté admin : toutes les inscriptions par défaut affichées dans la section dédiée).
  const myEventRegistrations = !isAdmin
    ? (events || []).flatMap(ev => {
        // Match strict par user_id (cas normal) OU fallback par email (legacy: inscriptions
        // admin créées avant le fix où user_id n'était pas relié au compte client).
        const myEmail = (user.email || '').toLowerCase();
        const regs = (ev.registrations || []).filter(r => {
          if (r.status === 'cancelled') return false;
          if (r.user_id && r.user_id === user.user_id) return true;
          if (!r.user_id && myEmail && (r.email || '').toLowerCase() === myEmail) return true;
          return false;
        });
        return regs.map(r => ({
          appointment_id: `event_${ev.event_id}_${r.registration_id || r.user_id}`,
          event_id: ev.event_id,
          registration_id: r.registration_id,
          service_name: ev.event_type?.name || ev.name || 'Atelier',
          event_subtitle: ev.subtitle,
          date: ev.date,
          time_slot: ev.time,
          duration_minutes: ev.event_type?.duration_minutes || 120,
          status: r.status === 'pending_payment' ? 'pending_payment' : 'confirmed',
          payment_status: r.paid ? 'paid' : 'unpaid',
          price: ev.price || ev.event_type?.price || 0,
          customer_name: r.name || user.name,
          customer_email: r.email || user.email,
          customer_phone: r.phone || user.phone,
          location: ev.location || '20 quai du Roubion, 26200 Montélimar',
          is_event: true,
        }));
      })
    : [];

  const upcomingApts = [...appointments.filter(a => a.status === 'confirmed' || a.status === 'pending_payment'), ...myEventRegistrations]
    .sort((x, y) => {
      const dx = (x.date || '') + ' ' + (x.time_slot || '');
      const dy = (y.date || '') + ' ' + (y.time_slot || '');
      return dx.localeCompare(dy);
    });
  const pastApts = appointments
    .filter(a => a.status === 'completed' || a.status === 'cancelled' || a.status === 'no_show')
    .sort((x, y) => {
      const dx = (x.date || '') + ' ' + (x.time_slot || '');
      const dy = (y.date || '') + ' ' + (y.time_slot || '');
      return dy.localeCompare(dx); // historique : plus recent en premier
    });

  return (
    <main className="pt-24 min-h-screen bg-[#1B132B]">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-4xl mb-2">{isAdmin ? 'Tableau de bord' : 'Mon Espace'}</h1>
            <p className="text-[#B59ED1]">Bienvenue, {user.name}</p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && <SquareSyncButton />}
          </div>
        </div>

        {/* Admin Stats */}
        {isAdmin && stats && (() => {
          const todayStr = formatLocalDate(new Date());
          const todayCount = appointments.filter(a => a.date === todayStr && a.status !== 'cancelled').length;
          const pendingOrders = orders.filter(o => ['pending', 'paid'].includes(o.status)).length;
          const statCards = [
            { icon: <Package className="w-5 h-5 text-[#00E5FF]" />, label: `Commandes en attente`, value: pendingOrders, bg: 'bg-[#00E5FF]/20', action: () => setAdminTab('orders'), testid: 'stat-pending-orders' },
            { icon: <Calendar className="w-5 h-5 text-[#9D4CDD]" />, label: `RDV aujourd'hui`, value: todayCount, bg: 'bg-[#9D4CDD]/20', action: () => { setAdminTab('calendar'); setCalendarView('day'); setCalendarDate(new Date()); }, testid: 'stat-today-apts' },
            { icon: <Users className="w-5 h-5 text-green-500" />, label: 'Clients', value: stats.total_users || 0, bg: 'bg-green-500/20', action: () => { setAdminTab('clients'); fetchClients(); }, testid: 'stat-clients' },
          ];
          return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {statCards.map((s, i) => {
                const Wrap = s.action ? 'button' : 'div';
                return (
                  <Wrap
                    key={s.testid || s.label}
                    onClick={s.action || undefined}
                    className={`stat-card text-left ${s.action ? 'hover:bg-[#3A115E]/30 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#D926A9]/40' : ''}`}
                    data-testid={s.testid}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${s.bg} flex items-center justify-center`}>{s.icon}</div>
                      <div>
                        <p className="text-[#B59ED1] text-xs">{s.label}</p>
                        <p className="stat-value text-lg">{s.value}</p>
                      </div>
                    </div>
                  </Wrap>
                );
              })}
            </div>
          );
        })()}

        {/* Admin Tabs */}
        {isAdmin && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {[
              { id: 'calendar', label: 'Calendrier' },
              { id: 'appointments', label: 'Liste RDV' },
              { id: 'clients', label: 'Clients' },
              { id: 'availability', label: 'Disponibilités' },
              { id: 'services', label: 'Services' },
              { id: 'products', label: 'Produits' },
              { id: 'events', label: 'Événements' },
              { id: 'retreats', label: 'Séjours & Retraites' },
              { id: 'blog', label: 'Blog' },
              { id: 'promo', label: 'Codes promo' },
              { id: 'shop-cats', label: 'Catégories boutique' },
              { id: 'orders', label: 'Commandes' },
              { id: 'newsletter', label: 'Newsletter' },
              { id: 'notifications', label: 'Notifications' }
            ].map(t => (
              <button key={t.id} onClick={() => {
                  setAdminTab(t.id);
                  if (t.id === 'clients') fetchClients();
                  // Quand on clique sur "Liste RDV", on filtre sur aujourd'hui par defaut
                  if (t.id === 'appointments' && !dateFilter) {
                    const today = formatLocalDate(new Date());
                    setDateFilter(today);
                  }
                  // Quand on clique sur Calendrier, on revient a aujourd'hui
                  if (t.id === 'calendar') {
                    setCalendarDate(new Date());
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${adminTab === t.id ? 'bg-[#D926A9] text-white' : 'bg-[#261B3B] text-[#B59ED1] hover:text-white'}`}
                data-testid={`admin-tab-${t.id}`}>{t.label}</button>
            ))}
          </div>
        )}

        {/* ADMIN: Calendar View */}
        {isAdmin && adminTab === 'calendar' && (
          <div className="space-y-4">
            {/* Calendar Controls */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigateCalendar(-1)} className="text-[#B59ED1]" data-testid="cal-prev">&lt;</Button>
                <h2 className="font-serif text-xl min-w-[200px] text-center">
                  {calendarView === 'day' && calendarDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {calendarView === 'week' && (() => {
                    const week = getWeekDays(calendarDate);
                    return `${week[0].getDate()} — ${week[6].getDate()} ${MONTH_NAMES[week[6].getMonth()]} ${week[6].getFullYear()}`;
                  })()}
                  {calendarView === 'month' && `${MONTH_NAMES[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => navigateCalendar(1)} className="text-[#B59ED1]" data-testid="cal-next">&gt;</Button>
                <Button variant="ghost" size="sm" onClick={() => setCalendarDate(new Date())} className="text-[#00E5FF] text-xs">Aujourd'hui</Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex gap-1 bg-[#261B3B] rounded-lg p-1">
                  {['day', 'week', 'month'].map(v => (
                    <button key={v} onClick={() => setCalendarView(v)}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${calendarView === v ? 'bg-[#D926A9] text-white' : 'text-[#B59ED1] hover:text-white'}`}
                      data-testid={`cal-view-${v}`}>
                      {v === 'day' ? 'Jour' : v === 'week' ? 'Semaine' : 'Mois'}
                    </button>
                  ))}
                </div>
                {/* Zoom (day & week seulement) */}
                {calendarView !== 'month' && (
                  <div className="flex gap-1 bg-[#261B3B] rounded-lg p-1" data-testid="cal-zoom">
                    {[
                      { v: 'compact', label: 'Compact', hint: 'Tout voir' },
                      { v: 'normal', label: 'Normal', hint: 'Equilibre' },
                      { v: 'expanded', label: 'Zoom', hint: 'Detaille' },
                    ].map(z => (
                      <button key={z.v} onClick={() => setCalendarZoom(z.v)}
                        title={z.hint}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${calendarZoom === z.v ? 'bg-[#00E5FF] text-[#1B132B]' : 'text-[#B59ED1] hover:text-white'}`}
                        data-testid={`cal-zoom-${z.v}`}>
                        {z.label}
                      </button>
                    ))}
                  </div>
                )}
                <Button size="sm" className="gradient-primary text-xs" onClick={() => openNewApt({ date: formatDateKey(calendarDate) })} data-testid="cal-new-apt">
                  <Plus className="w-3 h-3 mr-1" />Nouveau RDV
                </Button>
              </div>
            </div>

            {/* Day View - Timeline proportionnelle avec zoom */}
            {calendarView === 'day' && (() => {
              const dateStr = formatDateKey(calendarDate);
              const dayApts = getAptsForDate(dateStr);
              const dayEvents = getEventsForDate(dateStr);
              const START_HOUR = 9;
              const END_HOUR = 22; // 09:00 -> 22:00 (13h de plage)
              // 3 niveaux de zoom : compact = tout voir sans scroll, normal, zoom
              const PX_PER_MIN = calendarZoom === 'compact' ? 0.7 : calendarZoom === 'normal' ? 1.2 : 2.0;
              const PX_PER_HOUR = 60 * PX_PER_MIN;
              const TOTAL_HEIGHT = (END_HOUR - START_HOUR) * PX_PER_HOUR;
              const showHalfLabels = calendarZoom !== 'compact';

              const toPx = (hhmm) => {
                if (!hhmm) return 0;
                const [h, m] = hhmm.split(':').map(Number);
                return ((h - START_HOUR) * 60 + m) * PX_PER_MIN;
              };
              const aptDuration = (a) => {
                if (a.duration_minutes) return a.duration_minutes;
                if (a.end_time && a.time_slot) {
                  const [sh, sm] = a.time_slot.split(':').map(Number);
                  const [eh, em] = a.end_time.split(':').map(Number);
                  return (eh * 60 + em) - (sh * 60 + sm);
                }
                return 60;
              };

              const hours = [];
              for (let h = START_HOUR; h < END_HOUR; h++) hours.push(h);

              return (
                <Card className="bg-[#261B3B] border-white/10">
                  <CardContent className={`pt-4 ${calendarZoom === 'compact' ? '' : 'overflow-y-auto max-h-[80vh]'}`}>
                    <div className="relative flex" data-testid="day-view-timeline" style={{ height: TOTAL_HEIGHT + 'px' }}>
                      {/* Colonne des heures */}
                      <div className="w-16 flex-shrink-0 relative">
                        {hours.map(h => (
                          <div key={h} className="absolute left-0 text-xs text-[#B59ED1] font-mono"
                            style={{ top: ((h - START_HOUR) * PX_PER_HOUR - 6) + 'px' }}>
                            {String(h).padStart(2, '0')}:00
                          </div>
                        ))}
                        {/* Labels 30 min plus discrets (masques en compact) */}
                        {showHalfLabels && hours.map(h => (
                          <div key={'h30-' + h} className="absolute left-0 text-[10px] text-[#B59ED1]/40 font-mono"
                            style={{ top: ((h - START_HOUR) * PX_PER_HOUR + 30 * PX_PER_MIN - 6) + 'px' }}>
                            {String(h).padStart(2, '0')}:30
                          </div>
                        ))}
                      </div>

                      {/* Grille calendrier */}
                      <div className="flex-1 relative border-l border-white/10 ml-2">
                        {/* Lignes horaires */}
                        {hours.map(h => (
                          <React.Fragment key={h}>
                            <div className="absolute left-0 right-0 border-t border-white/10"
                              style={{ top: ((h - START_HOUR) * PX_PER_HOUR) + 'px' }} />
                            <div className="absolute left-0 right-0 border-t border-dashed border-white/5"
                              style={{ top: ((h - START_HOUR) * PX_PER_HOUR + 30 * PX_PER_MIN) + 'px' }} />
                          </React.Fragment>
                        ))}

                        {/* Créneaux bloqués (partiels) — bandes rouges ciblées */}
                        {renderBlockBands(dateStr, toPx)}
                        {/* Journée entièrement bloquée */}
                        {isDayFullyBlocked(dateStr) && (
                          <div className="absolute inset-0 bg-red-500/10 border border-red-400/30 rounded flex items-center justify-center pointer-events-none z-[1]">
                            <span className="text-sm text-red-300 font-medium bg-[#1B132B]/80 px-4 py-1.5 rounded-full">Journée bloquée</span>
                          </div>
                        )}

                        {/* RDV positionnes en absolu avec hauteur proportionnelle */}
                        {dayApts.map(a => {
                          const top = toPx(a.time_slot);
                          const dur = aptDuration(a);
                          const height = Math.max(dur * PX_PER_MIN - 2, 24);
                          const modeColor = a.location_mode === 'distance'
                            ? 'bg-[#00E5FF]/20 border-[#00E5FF]/50 text-white'
                            : 'bg-[#D926A9]/25 border-[#D926A9]/60 text-white';
                          return (
                            <div key={a.appointment_id}
                              className={`absolute left-1 right-1 px-2 py-1 rounded border-l-4 cursor-pointer hover:brightness-125 transition shadow-lg ${modeColor}`}
                              style={{ top: top + 'px', height: height + 'px', zIndex: 2 }}
                              onClick={() => setAptDetailModal(a)}
                              data-testid={`cal-apt-${a.appointment_id}`}
                              title={`${a.time_slot} → ${a.end_time} — ${a.service_name || a.service?.name}${a.customer_name ? ' (' + a.customer_name + ')' : ''}`}
                            >
                              <div className="text-[10px] font-mono opacity-90 leading-tight">
                                {a.time_slot} — {a.end_time} · {dur} min
                              </div>
                              <div className="text-xs font-semibold truncate leading-tight mt-0.5">
                                {a.service_name || a.service?.name}
                              </div>
                              {a.customer_name && height > 48 && (
                                <div className="text-[11px] opacity-90 truncate leading-tight">
                                  {a.customer_name}
                                </div>
                              )}
                              {a.variation_name && height > 72 && (
                                <div className="text-[10px] opacity-70 truncate italic leading-tight">
                                  {a.variation_name}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Évènements & ateliers — block dédié + buffer 60min de préparation */}
                        {dayEvents.map(ev => {
                          const evTime = ev.time || '19:00';
                          const evDur = ev.event_type?.duration_minutes || 120;
                          const evTop = toPx(evTime);
                          const evHeight = Math.max(evDur * PX_PER_MIN - 2, 30);
                          // Buffer de préparation 60 min avant l'événement
                          const bufferTop = Math.max(toPx(evTime) - 60 * PX_PER_MIN, 0);
                          const bufferHeight = toPx(evTime) - bufferTop;
                          const evName = ev.event_type?.name || 'Événement';
                          return (
                            <React.Fragment key={ev.event_id}>
                              {/* Zone de préparation (60 min) */}
                              {bufferHeight > 0 && (
                                <div
                                  className="absolute left-1 right-1 rounded border border-dashed border-amber-400/40 bg-amber-400/5 pointer-events-none"
                                  style={{ top: bufferTop + 'px', height: bufferHeight + 'px', zIndex: 1 }}
                                  title={`Préparation ${evName} (60 min)`}
                                >
                                  <div className="text-[9px] text-amber-300/80 font-mono px-1.5 pt-0.5">
                                    ⏱ Prép. {evName} (60 min)
                                  </div>
                                </div>
                              )}
                              {/* Événement lui-même */}
                              <div
                                className="absolute left-1 right-1 px-2 py-1 rounded border-l-4 bg-amber-500/25 border-amber-400 text-white shadow-lg cursor-pointer hover:brightness-125 transition"
                                style={{ top: evTop + 'px', height: evHeight + 'px', zIndex: 3 }}
                                title={`${evTime} — ${evName}${ev.subtitle ? ' · ' + ev.subtitle : ''} — cliquer pour le résumé`}
                                onClick={(e) => { e.stopPropagation(); setEventDetailModal(ev); }}
                                data-testid={`cal-event-${ev.event_id}`}
                              >
                                <div className="text-[10px] font-mono opacity-90 leading-tight flex items-center gap-1">
                                  ✨ {evTime} · {evDur} min
                                </div>
                                <div className="text-xs font-semibold truncate leading-tight mt-0.5">
                                  {evName}
                                </div>
                                {ev.subtitle && evHeight > 48 && (
                                  <div className="text-[10px] opacity-80 truncate italic leading-tight">
                                    {ev.subtitle}
                                  </div>
                                )}
                                {evHeight > 64 && (
                                  <div className="text-[10px] opacity-70 leading-tight mt-0.5">
                                    {(ev.registrations || []).length}/{ev.max_participants} inscrits
                                  </div>
                                )}
                              </div>
                            </React.Fragment>
                          );
                        })}

                        {/* Ligne "maintenant" */}
                        {dateStr === formatDateKey(new Date()) && (() => {
                          const now = new Date();
                          const nowMin = (now.getHours() - START_HOUR) * 60 + now.getMinutes();
                          const nowPx = nowMin * PX_PER_MIN;
                          if (nowPx < 0 || nowPx > TOTAL_HEIGHT) return null;
                          return (
                            <div className="absolute left-0 right-0 pointer-events-none z-10"
                              style={{ top: nowPx + 'px' }}>
                              <div className="flex items-center">
                                <div className="w-2.5 h-2.5 rounded-full bg-[#00E5FF] -ml-1.5 ring-2 ring-[#00E5FF]/30" />
                                <div className="flex-1 h-px bg-[#00E5FF]" />
                                <span className="text-[10px] text-[#00E5FF] ml-1 font-mono">
                                  {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Week View - Chaque jour en mini-timeline proportionnelle avec zoom */}
            {calendarView === 'week' && (() => {
              const weekDays = getWeekDays(calendarDate);
              const today = formatDateKey(new Date());
              const START_HOUR = 9;
              const END_HOUR = 22;
              const PX_PER_MIN = calendarZoom === 'compact' ? 0.55 : calendarZoom === 'normal' ? 1.0 : 1.5;
              const PX_PER_HOUR = 60 * PX_PER_MIN;
              const TOTAL_HEIGHT = (END_HOUR - START_HOUR) * PX_PER_HOUR;
              const HEADER_HEIGHT = 56; // hauteur exacte de l'en-tete (JOUR + numero + border)
              const aptDuration = (a) => {
                if (a.duration_minutes) return a.duration_minutes;
                if (a.end_time && a.time_slot) {
                  const [sh, sm] = a.time_slot.split(':').map(Number);
                  const [eh, em] = a.end_time.split(':').map(Number);
                  return (eh * 60 + em) - (sh * 60 + sm);
                }
                return 60;
              };
              const toPx = (hhmm) => {
                if (!hhmm) return 0;
                const [h, m] = hhmm.split(':').map(Number);
                return ((h - START_HOUR) * 60 + m) * PX_PER_MIN;
              };
              const hours = [];
              for (let h = START_HOUR; h < END_HOUR; h++) hours.push(h);
              return (
                <Card className="bg-[#261B3B] border-white/10 overflow-x-auto">
                  <CardContent className={`pt-4 ${calendarZoom === 'compact' ? '' : 'overflow-y-auto max-h-[80vh]'}`}>
                    <div className="flex min-w-[760px]">
                      {/* Colonne des heures (avec spacer pour aligner sur les grilles apres l'en-tete de jour) */}
                      <div className="w-14 flex-shrink-0">
                        <div style={{ height: HEADER_HEIGHT + 'px' }} />
                        <div className="relative" style={{ height: TOTAL_HEIGHT + 'px' }}>
                          {hours.map(h => (
                            <div key={h} className="absolute left-0 text-[11px] text-[#B59ED1] font-mono"
                              style={{ top: ((h - START_HOUR) * PX_PER_HOUR - 6) + 'px' }}>
                              {String(h).padStart(2, '0')}:00
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Colonnes des jours */}
                      <div className="flex-1 grid grid-cols-7 gap-px">
                        {weekDays.map((d, i) => {
                          const dateStr = formatDateKey(d);
                          const dayApts = getAptsForDate(dateStr);
                          const dayEvts = getEventsForDate(dateStr);
                          const isToday = dateStr === today;
                          const allDayBlocked = isDayFullyBlocked(dateStr);
                          return (
                            <div key={i} className={`rounded-lg overflow-hidden ${isToday ? 'bg-[#D926A9]/10 border border-[#D926A9]/30' : 'bg-[#1B132B]'} ${allDayBlocked ? 'opacity-40' : ''}`}>
                              <div className="text-center py-2 border-b border-white/5 cursor-pointer hover:bg-white/5"
                                style={{ height: HEADER_HEIGHT + 'px' }}
                                onClick={() => { setCalendarDate(d); setCalendarView('day'); }}>
                                <p className="text-[10px] text-[#B59ED1] uppercase">{DAY_NAMES_SHORT[i]}</p>
                                <p className={`text-lg font-medium leading-tight ${isToday ? 'text-[#D926A9]' : ''}`}>{d.getDate()}</p>
                              </div>
                              <div className="relative" style={{ height: TOTAL_HEIGHT + 'px' }}>
                                {hours.map(h => (
                                  <React.Fragment key={h}>
                                    <div className="absolute left-0 right-0 border-t border-white/5"
                                      style={{ top: ((h - START_HOUR) * PX_PER_HOUR) + 'px' }} />
                                    <div className="absolute left-0 right-0 border-t border-dashed border-white/5"
                                      style={{ top: ((h - START_HOUR) * PX_PER_HOUR + 30 * PX_PER_MIN) + 'px' }} />
                                  </React.Fragment>
                                ))}
                                {/* Créneaux bloqués (partiels) — bandes rouges ciblées */}
                                {renderBlockBands(dateStr, toPx)}
                                {/* Événements + buffer 60 min de préparation */}
                                {dayEvts.map(ev => {
                                  const evTime = ev.time || '19:00';
                                  const evDur = ev.event_type?.duration_minutes || 120;
                                  const evTop = toPx(evTime);
                                  const evHeight = Math.max(evDur * PX_PER_MIN - 1, 20);
                                  const bufferTop = Math.max(evTop - 60 * PX_PER_MIN, 0);
                                  const bufferHeight = evTop - bufferTop;
                                  const evName = ev.event_type?.name || 'Événement';
                                  return (
                                    <React.Fragment key={ev.event_id}>
                                      {bufferHeight > 0 && (
                                        <div className="absolute left-0.5 right-0.5 rounded border border-dashed border-amber-400/40 bg-amber-400/5 pointer-events-none"
                                          style={{ top: bufferTop + 'px', height: bufferHeight + 'px', zIndex: 1 }}
                                          title={`Préparation ${evName}`}
                                        />
                                      )}
                                      <div className="absolute left-0.5 right-0.5 px-1 py-0.5 rounded border-l-2 bg-amber-500/30 border-amber-400 text-white overflow-hidden cursor-pointer hover:brightness-125 transition"
                                        style={{ top: evTop + 'px', height: evHeight + 'px', zIndex: 3 }}
                                        title={`${evTime} — ${evName}${ev.subtitle ? ' · ' + ev.subtitle : ''} — cliquer pour le résumé`}
                                        onClick={(e) => { e.stopPropagation(); setEventDetailModal(ev); }}
                                        data-testid={`cal-week-event-${ev.event_id}`}
                                      >
                                        <div className="text-[9px] font-mono opacity-90 leading-none">✨ {evTime}</div>
                                        <div className="text-[10px] truncate font-semibold leading-tight">{evName}</div>
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                                {dayApts.map(a => {
                                  const top = toPx(a.time_slot);
                                  const height = Math.max(aptDuration(a) * PX_PER_MIN - 1, 18);
                                  const modeColor = a.location_mode === 'distance'
                                    ? 'bg-[#00E5FF]/25 border-[#00E5FF]/50 text-white'
                                    : 'bg-[#D926A9]/30 border-[#D926A9]/60 text-white';
                                  return (
                                    <div key={a.appointment_id}
                                      className={`absolute left-0.5 right-0.5 px-1 py-0.5 rounded border-l-2 cursor-pointer overflow-hidden hover:brightness-125 transition ${modeColor}`}
                                      style={{ top: top + 'px', height: height + 'px', zIndex: 2 }}
                                      onClick={() => setAptDetailModal(a)}
                                      data-testid={`cal-apt-${a.appointment_id}`}
                                      title={`${a.time_slot} → ${a.end_time} — ${a.service_name || a.service?.name}${a.customer_name ? ' (' + a.customer_name + ')' : ''}`}
                                    >
                                      <div className="text-[9px] font-mono opacity-90 leading-none">{a.time_slot}</div>
                                      <div className="text-[10px] truncate font-semibold leading-tight">{a.service_name || a.service?.name}</div>
                                      {a.customer_name && height > 34 && <div className="text-[9px] truncate opacity-85 leading-tight">{a.customer_name}</div>}
                                    </div>
                                  );
                                })}
                                {allDayBlocked && <div className="absolute inset-0 flex items-center justify-center text-[10px] text-red-400">Bloqué</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Month View */}
            {calendarView === 'month' && (() => {
              const monthDays = getMonthDays(calendarDate);
              const today = formatDateKey(new Date());
              const currentMonth = calendarDate.getMonth();
              return (
                <Card className="bg-[#261B3B] border-white/10">
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-7 gap-px">
                      {DAY_NAMES_SHORT.map(d => <div key={d} className="text-center text-[10px] text-[#B59ED1] uppercase pb-2">{d}</div>)}
                      {monthDays.map((d, i) => {
                        if (!d) {
                          // Cellule vide (avant le 1er du mois) — espace réservé pour aligner la grille
                          return <div key={`empty-${i}`} className="p-1 min-h-[80px]" />;
                        }
                        const dateStr = formatDateKey(d);
                        const dayApts = getAptsForDate(dateStr);
                        const dayEvts = getEventsForDate(dateStr);
                        const isToday = dateStr === today;
                        const isCurrentMonth = d.getMonth() === currentMonth;
                        return (
                          <div key={dateStr} className={`p-1 min-h-[80px] rounded ${isToday ? 'bg-[#D926A9]/10 border border-[#D926A9]/30' : 'bg-[#1B132B]'} ${!isCurrentMonth ? 'opacity-30' : ''}`}
                            onClick={() => { setCalendarDate(d); setCalendarView('day'); }}>
                            <p className={`text-xs mb-1 ${isToday ? 'text-[#D926A9] font-bold' : 'text-[#B59ED1]'}`}>{d.getDate()}</p>
                            {dayEvts.slice(0, 1).map(ev => (
                              <div key={ev.event_id} className="text-[9px] bg-amber-500/25 border-l-2 border-amber-400 rounded px-1 mb-0.5 truncate text-amber-100 font-semibold cursor-pointer hover:brightness-125" title={`${ev.event_type?.name || 'Événement'} — cliquer pour le résumé`}
                                onClick={(e) => { e.stopPropagation(); setEventDetailModal(ev); }}
                                data-testid={`cal-month-event-${ev.event_id}`}>
                                ✨ {ev.time} {ev.event_type?.name?.split(' ')[0]}
                              </div>
                            ))}
                            {dayApts.slice(0, 2).map(a => (
                              <div key={a.appointment_id} className="text-[9px] bg-[#D926A9]/15 rounded px-1 mb-0.5 truncate text-[#E2D5F8]">
                                {a.time_slot} {a.service_name?.split(' ')[0]}
                              </div>
                            ))}
                            {(dayApts.length + dayEvts.length) > 3 && <p className="text-[9px] text-[#B59ED1]">+{(dayApts.length + dayEvts.length) - 3}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Rappels 24h : envoi automatique tous les jours à 9h (Paris) via le scheduler backend.
                Plus besoin de bouton manuel. */}
            <div className="flex justify-end">
              <span className="inline-flex items-center gap-2 text-xs text-[#B59ED1] px-3 py-2 rounded-md bg-[#D926A9]/5 border border-[#D926A9]/15" data-testid="reminders-auto-badge">
                <Mail className="w-3 h-3 text-[#D926A9]" />
                Rappels 24h envoyés automatiquement chaque jour à 9h
              </span>
            </div>
          </div>
        )}

        {/* CLIENT VIEW or ADMIN APPOINTMENTS TAB */}
        {(!isAdmin || adminTab === 'appointments') && (
          <div className="space-y-6">
            {/* Admin filters */}
            {isAdmin && (
              <div className="flex gap-3 flex-wrap items-center mb-4">
                <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="bg-[#261B3B] border-white/10 w-44" placeholder="Filtrer par date" data-testid="filter-date" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-[#261B3B] border-white/10 w-44" data-testid="filter-status"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    <SelectItem value="all" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Tous les statuts</SelectItem>
                    <SelectItem value="confirmed" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Confirmés</SelectItem>
                    <SelectItem value="pending_payment" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">En attente</SelectItem>
                    <SelectItem value="completed" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Terminés</SelectItem>
                    <SelectItem value="cancelled" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Annulés</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={serviceFilter} onValueChange={setServiceFilter}>
                  <SelectTrigger className="bg-[#261B3B] border-white/10 w-52" data-testid="filter-service"><SelectValue placeholder="Tous les services" /></SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    <SelectItem value="all" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Tous les services</SelectItem>
                    {services.map(s => (
                      <SelectItem key={s.service_id} value={s.service_id} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(dateFilter || serviceFilter) && <Button variant="ghost" size="sm" className="text-[#B59ED1]" onClick={() => { setDateFilter(''); setStatusFilter(''); setServiceFilter(''); }}>Réinitialiser</Button>}
                <Button size="sm" className="gradient-primary ml-auto" onClick={() => openNewApt({ date: dateFilter || formatLocalDate(new Date()) })} data-testid="list-new-apt">
                  <Plus className="w-3 h-3 mr-1" />Nouveau RDV
                </Button>
              </div>
            )}

            {/* Notifications push (client) */}
            {!isAdmin && (
              <Card className="bg-[#261B3B] border-[#00E5FF]/20" data-testid="push-card-client">
                <CardContent className="pt-5 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-serif text-lg flex items-center gap-2 mb-1">
                      <Bell className="w-4 h-4 text-[#00E5FF]" /> Notifications
                    </p>
                    <p className="text-xs text-[#B59ED1]">Reçois rappels de RDV et infos de commande directement sur ton téléphone.</p>
                  </div>
                  <PushSubscribeButton compact />
                </CardContent>
              </Card>
            )}

            {/* Upcoming */}
            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-[#D926A9]" />{isAdmin ? 'Rendez-vous à venir' : 'Mes rendez-vous à venir'} ({upcomingApts.length})</CardTitle></CardHeader>
              <CardContent>
                {loading ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-lg skeleton"></div>)}</div>
                : upcomingApts.length === 0 ? <p className="text-[#B59ED1] text-center py-6 text-sm">Aucun rendez-vous à venir</p>
                : <div className="space-y-3">{upcomingApts.map(apt => (
                  <div key={apt.appointment_id} className={`p-4 rounded-lg ${apt.is_event ? 'bg-amber-500/5 border border-amber-400/20' : 'bg-[#1B132B]'}`} data-testid={`apt-${apt.appointment_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {apt.is_event && <span className="text-amber-300 text-sm" title="Atelier / Événement">✨</span>}
                          <h4 className="font-medium text-sm">{apt.service_name || apt.service?.name}</h4>
                          {apt.variation_name && <span className="text-[#B59ED1] text-xs">({apt.variation_name})</span>}
                          {apt.event_subtitle && <span className="text-[#B59ED1] text-xs italic">— {apt.event_subtitle}</span>}
                          <Badge className={`text-[10px] ${apt.is_event ? 'bg-amber-500/20 text-amber-200' : statusColor(apt.status)}`}>
                            {apt.is_event ? 'Atelier' : statusLabel(apt.status)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-[#B59ED1] flex-wrap">
                          <span className="capitalize">{new Date(apt.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                          <span>{apt.time_slot}{apt.end_time ? ` — ${apt.end_time}` : (apt.duration_minutes ? ` (${apt.duration_minutes} min)` : '')}</span>
                          {!apt.is_event && (
                            <Badge className={`text-[10px] ${apt.location_mode === 'distance' ? 'bg-[#00E5FF]/10 text-[#00E5FF]' : 'bg-[#D926A9]/10 text-[#D926A9]'}`}>
                              {apt.location_mode === 'distance' ? 'Distance' : 'Boutique'}
                            </Badge>
                          )}
                          <span className="text-xs">{paymentLabel(apt.payment_status)}</span>
                        </div>
                        {isAdmin && apt.customer_name && <p className="text-xs text-[#B59ED1] mt-1">{apt.customer_name} — {apt.customer_email}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[#D926A9] font-semibold text-sm">{apt.price} €</span>
                        {/* Bouton "Payer maintenant" si RDV en attente de paiement (client uniquement) */}
                        {!isAdmin && !apt.is_event && apt.status === 'pending_payment' && (
                          <Button
                            size="sm"
                            className="gradient-primary text-xs h-7 px-2.5"
                            onClick={() => setPayModal({
                              kind: 'appointment',
                              id: apt.appointment_id,
                              amount: apt.price,
                              title: `${apt.service_name || ''} — ${new Date(apt.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${apt.time_slot}`,
                            })}
                            data-testid={`pay-apt-${apt.appointment_id}`}
                          >
                            Payer
                          </Button>
                        )}
                        {!isAdmin && apt.is_event && apt.status === 'pending_payment' && (
                          <Button
                            size="sm"
                            className="gradient-primary text-xs h-7 px-2.5"
                            onClick={() => setPayModal({
                              kind: 'event',
                              id: apt.event_id,
                              amount: apt.price,
                              title: `${apt.service_name} — ${new Date(apt.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${apt.time_slot}`,
                            })}
                            data-testid={`pay-event-${apt.event_id}`}
                          >
                            Payer
                          </Button>
                        )}
                        {!apt.is_event && (canModify(apt) || (isAdmin && apt.status !== 'completed' && apt.status !== 'cancelled')) && (
                          <>
                            <Button variant="ghost" size="sm" className="text-[#00E5FF] text-xs" onClick={() => setRescheduleModal(apt)} data-testid={`reschedule-${apt.appointment_id}`}><Settings className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-400 text-xs" onClick={() => cancelAppointment(apt.appointment_id)} data-testid={`cancel-${apt.appointment_id}`}><Trash2 className="w-3 h-3" /></Button>
                          </>
                        )}
                        {/* Admin : peut annuler même un RDV "terminé" (clic accidentel).
                            canModify est déjà vrai pour les RDV confirmés futurs → on
                            n'ajoute le bouton annuler QUE pour les statuts terminaux
                            (completed) afin d'éviter les doublons. */}
                        {isAdmin && !apt.is_event && apt.status === 'completed' && (
                          <>
                            <Button variant="ghost" size="sm" className="text-yellow-400 text-xs" title="Rétablir en Confirmé" onClick={() => restoreAppointment(apt.appointment_id)} data-testid={`restore-${apt.appointment_id}`}><RotateCcw className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-400 text-xs" onClick={() => cancelAppointment(apt.appointment_id)} data-testid={`cancel-${apt.appointment_id}`}><Trash2 className="w-3 h-3" /></Button>
                          </>
                        )}
                        {isAdmin && apt.status === 'confirmed' && (
                          <Button variant="ghost" size="sm" className="text-green-400 text-xs" onClick={() => completeAppointment(apt.appointment_id)} data-testid={`complete-${apt.appointment_id}`}><Check className="w-3 h-3" /></Button>
                        )}
                        {/* Admin : encaissement manuel (RDV impayé dont le lien en ligne a échoué) */}
                        {isAdmin && !apt.is_event && apt.status !== 'cancelled' && apt.payment_status !== 'paid' && (
                          <Button variant="ghost" size="sm" className="text-emerald-400 text-xs" title="Marquer comme payé (encaissement manuel)" onClick={() => markAppointmentPaid(apt.appointment_id)} data-testid={`mark-paid-${apt.appointment_id}`}><CreditCard className="w-3 h-3" /></Button>
                        )}
                      </div>
                    </div>
                    {apt.notes && <p className="text-xs text-[#B59ED1] mt-2 italic">Note : {apt.notes}</p>}
                  </div>
                ))}</div>}
                {!isAdmin && <Button onClick={() => navigate('/booking')} className="w-full mt-4 gradient-primary" data-testid="new-appointment">Nouveau rendez-vous</Button>}
              </CardContent>
            </Card>

            {/* Past appointments */}
            {pastApts.length > 0 && (
              <Card className="bg-[#261B3B] border-white/10">
                <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2 text-[#B59ED1]"><Clock className="w-5 h-5" />Historique ({pastApts.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">{pastApts.slice(0, 10).map(apt => (
                    <div key={apt.appointment_id} className="p-3 bg-[#1B132B] rounded-lg flex items-center justify-between opacity-70">
                      <div>
                        <span className="font-medium text-sm">{apt.service_name || apt.service?.name}</span>
                        <span className="text-[#B59ED1] text-xs ml-2">{new Date(apt.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} à {apt.time_slot}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{apt.price} €</span>
                        <Badge className={`text-[10px] ${statusColor(apt.status)}`}>{statusLabel(apt.status)}</Badge>
                      </div>
                    </div>
                  ))}</div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ADMIN: Availability tab */}
        {isAdmin && adminTab === 'availability' && (
          <div className="space-y-6">
            {/* Shop Hours Editor */}
            <Card className="bg-[#261B3B] border-white/10" data-testid="shop-hours-editor">
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#00E5FF]" />
                  Horaires d'ouverture boutique
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editShopHours && (
                  <div className="space-y-3">
                    {Object.entries(editShopHours).sort(([a], [b]) => Number(a) - Number(b)).map(([day, hours]) => (
                      <div key={day} className="flex items-center gap-3 p-3 bg-[#1B132B] rounded-lg" data-testid={`shop-day-${day}`}>
                        <span className="w-24 text-sm font-medium">{DAY_LABELS[day]}</span>
                        <div className="flex items-center gap-2 flex-1">
                          <Input type="time" value={hours.open} onChange={(e) => updateShopHour(day, 'open', e.target.value)}
                            className="bg-[#261B3B] border-white/10 w-32" data-testid={`shop-open-${day}`} />
                          <span className="text-[#B59ED1] text-sm">à</span>
                          <Input type="time" value={hours.close} onChange={(e) => updateShopHour(day, 'close', e.target.value)}
                            className="bg-[#261B3B] border-white/10 w-32" data-testid={`shop-close-${day}`} />
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-400" onClick={() => removeShopDay(day)} data-testid={`remove-day-${day}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}

                    {/* Add day button */}
                    {Object.keys(editShopHours).length < 7 && (
                      <div className="flex gap-2 flex-wrap">
                        {[0,1,2,3,4,5,6].filter(d => !editShopHours[d] && !editShopHours[String(d)]).map(d => (
                          <Button key={d} variant="outline" size="sm" className="border-white/10 text-[#B59ED1] text-xs" onClick={() => addShopDay(d)} data-testid={`add-day-${d}`}>
                            + {DAY_LABELS[d]}
                          </Button>
                        ))}
                      </div>
                    )}

                    <Button onClick={saveShopHours} disabled={savingHours} className="gradient-primary w-full" data-testid="save-shop-hours">
                      {savingHours ? 'Enregistrement...' : 'Enregistrer les horaires'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recurring Blocks */}
            <Card className="bg-[#261B3B] border-white/10" data-testid="recurring-blocks">
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-[#D926A9]" />
                  Blocages récurrents
                </CardTitle>
                <p className="text-xs text-[#B59ED1] mt-1">Bloquer automatiquement un jour ou une plage horaire chaque semaine</p>
              </CardHeader>
              <CardContent>
                {/* Add recurring form */}
                <div className="p-4 bg-[#1B132B] rounded-lg mb-4 space-y-3">
                  <div className="flex gap-3 flex-wrap items-end">
                    <div>
                      <label className="text-xs text-[#B59ED1] mb-1 block">Jour</label>
                      <Select value={recDay} onValueChange={setRecDay}>
                        <SelectTrigger className="bg-[#261B3B] border-white/10 w-40" data-testid="rec-day"><SelectValue placeholder="Jour" /></SelectTrigger>
                        <SelectContent className="bg-[#261B3B] border-white/10">
                          {[0,1,2,3,4,5,6].map(d => (
                            <SelectItem key={d} value={String(d)} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">{DAY_LABELS[d]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-xs text-[#B59ED1] mb-1">
                        <input type="checkbox" checked={recAllDay} onChange={(e) => setRecAllDay(e.target.checked)} className="rounded" data-testid="rec-allday" />
                        Toute la journée
                      </label>
                    </div>
                    {!recAllDay && (
                      <>
                        <div>
                          <label className="text-xs text-[#B59ED1] mb-1 block">De</label>
                          <Input type="time" value={recTimeFrom} onChange={(e) => setRecTimeFrom(e.target.value)} className="bg-[#261B3B] border-white/10 w-28" data-testid="rec-time-from" />
                        </div>
                        <div>
                          <label className="text-xs text-[#B59ED1] mb-1 block">À</label>
                          <Input type="time" value={recTimeTo} onChange={(e) => setRecTimeTo(e.target.value)} className="bg-[#261B3B] border-white/10 w-28" data-testid="rec-time-to" />
                        </div>
                      </>
                    )}
                    <div className="flex-1">
                      <label className="text-xs text-[#B59ED1] mb-1 block">Raison</label>
                      <Input value={recReason} onChange={(e) => setRecReason(e.target.value)} placeholder="Ex: Formation, Repos..." className="bg-[#261B3B] border-white/10" data-testid="rec-reason" />
                    </div>
                    <Button onClick={handleAddRecurring} className="gradient-primary" data-testid="add-recurring-btn">Ajouter</Button>
                  </div>
                </div>

                {/* List */}
                {recurringBlocks.length === 0 ? (
                  <p className="text-[#B59ED1] text-center py-4 text-sm">Aucun blocage récurrent</p>
                ) : (
                  <div className="space-y-2">
                    {recurringBlocks.map(r => (
                      <div key={r.recurring_id} className="flex items-center justify-between p-3 bg-[#1B132B] rounded-lg" data-testid={`recurring-${r.recurring_id}`}>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-[#D926A9]/20 text-[#D926A9] text-xs">{DAY_LABELS[r.day_of_week]}</Badge>
                          {r.all_day ? (
                            <span className="text-sm text-red-400">Toute la journée</span>
                          ) : (
                            <span className="text-sm text-[#E2D5F8]">{formatHour(r.time_from)} — {formatHour(r.time_to)}</span>
                          )}
                          {r.reason && <span className="text-xs text-[#B59ED1]">— {r.reason}</span>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDeleteRecurring(r.recurring_id)} data-testid={`delete-recurring-${r.recurring_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader><CardTitle className="font-serif text-lg">Bloquer un jour ou un créneau ponctuel</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setBlockAllDay(true)}
                    className={blockAllDay ? "gradient-primary" : "bg-[#1B132B] border border-white/10 hover:bg-[#1B132B]/70"}
                    data-testid="block-mode-day"
                  >Journée entière</Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setBlockAllDay(false)}
                    className={!blockAllDay ? "gradient-primary" : "bg-[#1B132B] border border-white/10 hover:bg-[#1B132B]/70"}
                    data-testid="block-mode-slot"
                  >Plage horaire</Button>
                </div>
                <div className="flex gap-3 flex-wrap items-end">
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Du</label>
                    <Input type="date" value={blockDate} onChange={(e) => { setBlockDate(e.target.value); if (!blockDateTo) setBlockDateTo(e.target.value); }} className="bg-[#1B132B] border-white/10 w-44" data-testid="block-date" />
                  </div>
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Au (optionnel)</label>
                    <Input type="date" value={blockDateTo} onChange={(e) => setBlockDateTo(e.target.value)} min={blockDate} className="bg-[#1B132B] border-white/10 w-44" data-testid="block-date-to" />
                  </div>
                  {!blockAllDay && (
                    <>
                      <div>
                        <label className="text-xs text-[#B59ED1] mb-1 block">De (HH:MM)</label>
                        <Input type="time" value={blockTimeFrom} onChange={(e) => setBlockTimeFrom(e.target.value)} step={900} className="bg-[#1B132B] border-white/10 w-28" data-testid="block-time-from" />
                      </div>
                      <div>
                        <label className="text-xs text-[#B59ED1] mb-1 block">À (HH:MM)</label>
                        <Input type="time" value={blockTimeTo} onChange={(e) => setBlockTimeTo(e.target.value)} step={900} className="bg-[#1B132B] border-white/10 w-28" data-testid="block-time-to" />
                      </div>
                    </>
                  )}
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs text-[#B59ED1] mb-1 block">Raison (optionnel)</label>
                    <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder={blockAllDay ? "Vacances, fermeture..." : "Rendez-vous perso, formation..."} className="bg-[#1B132B] border-white/10" data-testid="block-reason" />
                  </div>
                  <Button onClick={handleBlockDay} className="gradient-primary" data-testid="block-day-btn">Bloquer</Button>
                </div>
                {!blockAllDay && (
                  <p className="text-xs text-[#B59ED1]/70 mt-3">
                    💡 Le créneau sera bloqué uniquement pour la(les) date(s) sélectionnée(s), pas en récurrence. Pour un blocage hebdomadaire, utilise la section "Blocages récurrents" plus haut.
                  </p>
                )}
              </CardContent>
            </Card>
            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader><CardTitle className="font-serif text-lg">Jours et créneaux bloqués</CardTitle></CardHeader>
              <CardContent>
                {blockedSlots.length === 0 ? <p className="text-[#B59ED1] text-center py-4 text-sm">Aucun blocage</p>
                : <div className="space-y-2">{blockedSlots.map(b => {
                  const isSlot = !b.all_day;
                  const slotLabel = b.time_from && b.time_to
                    ? `${b.time_from} – ${b.time_to}`
                    : (b.time_slot || '');
                  return (
                  <div key={b.block_id} className="flex items-center justify-between p-3 bg-[#1B132B] rounded-lg">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{b.date}</span>
                      {isSlot ? (
                        <Badge className="bg-[#D926A9]/20 text-[#D926A9] text-xs">{slotLabel || 'créneau'}</Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 text-xs">Journée entière</Badge>
                      )}
                      {b.reason && <span className="text-[#B59ED1] text-xs">— {b.reason}</span>}
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleUnblock(b.block_id)} data-testid={`unblock-${b.block_id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  );
                })}</div>}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ADMIN: Services/Events tabs */}
        {isAdmin && adminTab === 'services' && <AdminServiceManager />}
        {isAdmin && adminTab === 'products' && <AdminProductsManager />}
        {isAdmin && adminTab === 'events' && <AdminEventManager />}
        {isAdmin && adminTab === 'retreats' && <AdminRetreatsManager onOpenClient={openClientDetail} />}

        {/* ADMIN: Blog tab */}
        {isAdmin && adminTab === 'blog' && <AdminBlogManager />}

        {/* ADMIN: Promo Codes tab */}
        {isAdmin && adminTab === 'promo' && <AdminPromoManager />}

        {/* ADMIN: Catégories boutique (groupes éditoriaux) */}
        {isAdmin && adminTab === 'shop-cats' && <AdminMainCategoriesManager />}

        {/* ADMIN: Clients tab — recherche + tri alphabétique + pagination */}
        {isAdmin && adminTab === 'clients' && (() => {
          const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const q = normalize(clientSearch.trim());
          const filtered = (clients || [])
            .filter(c => !q || normalize(c.name).includes(q) || normalize(c.email).includes(q) || normalize(c.phone || '').includes(q))
            .sort((a, b) => normalize(a.name || a.email).localeCompare(normalize(b.name || b.email), 'fr'));
          const totalPages = Math.max(1, Math.ceil(filtered.length / CLIENTS_PER_PAGE));
          const safePage = Math.min(clientPage, totalPages);
          const start = (safePage - 1) * CLIENTS_PER_PAGE;
          const pageItems = filtered.slice(start, start + CLIENTS_PER_PAGE);
          return (
            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader className="space-y-3">
                <CardTitle className="font-serif text-lg flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#00E5FF]" />
                    Clients <span className="text-sm text-[#B59ED1]">({filtered.length}{q ? ` / ${clients.length}` : ''})</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExportsOpen(true)}
                      className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF] text-xs"
                      data-testid="open-csv-exports-btn"
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Exports CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setWixImportOpen(true)}
                      className="border-[#D926A9]/40 text-[#D926A9] hover:bg-[#D926A9]/10 hover:text-[#D926A9] text-xs"
                      data-testid="open-wix-import-btn"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Importer (CSV Wix)
                    </Button>
                  </div>
                </CardTitle>
                <div className="relative">
                  <Input
                    type="search"
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setClientPage(1); }}
                    placeholder="Rechercher par nom, email ou téléphone…"
                    className="bg-[#1B132B] border-white/10 pl-9"
                    data-testid="clients-search-input"
                  />
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B59ED1]" />
                  {clientSearch && (
                    <button
                      onClick={() => { setClientSearch(''); setClientPage(1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#B59ED1] hover:text-white p-1"
                      aria-label="Effacer la recherche"
                      data-testid="clients-search-clear"
                    ><X className="w-4 h-4" /></button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {filtered.length === 0 ? (
                  <p className="text-[#B59ED1] text-center py-6 text-sm">
                    {q ? "Aucun client ne correspond à ta recherche." : "Aucun client"}
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">{pageItems.map(c => (
                      <button key={c.user_id} type="button" onClick={() => openClientDetail(c.user_id)} className="w-full text-left p-4 bg-[#1B132B] rounded-lg flex items-center gap-4 hover:bg-[#1B132B]/80 hover:border-[#00E5FF]/30 border border-transparent transition-all" data-testid={`client-${c.user_id}`}>
                        {c.picture && <img src={c.picture} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm">{c.name}</h4>
                          <p className="text-[#B59ED1] text-xs truncate">{c.email}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-[#00E5FF]">{c.appointment_count} RDV</p>
                          {c.last_appointment && <p className="text-[10px] text-[#B59ED1]">Dernier : {c.last_appointment.date}</p>}
                        </div>
                        <Badge className={`text-[10px] ${c.role === 'admin' ? 'bg-[#D926A9]/20 text-[#D926A9]' : 'bg-white/5 text-[#B59ED1]'}`}>
                          {c.role === 'admin' ? 'Admin' : 'Client'}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-[#B59ED1]" />
                      </button>
                    ))}</div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
                        <p className="text-xs text-[#B59ED1]">
                          {start + 1}–{Math.min(start + CLIENTS_PER_PAGE, filtered.length)} sur {filtered.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={safePage === 1}
                            onClick={() => setClientPage(p => Math.max(1, p - 1))}
                            className="border-white/10 text-[#E2D5F8] hover:bg-white/5 disabled:opacity-30"
                            data-testid="clients-prev-page"
                          >
                            ← Précédent
                          </Button>
                          <span className="text-xs text-[#B59ED1] px-2 tabular-nums">
                            Page <span className="text-white">{safePage}</span> / {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={safePage === totalPages}
                            onClick={() => setClientPage(p => Math.min(totalPages, p + 1))}
                            className="border-white/10 text-[#E2D5F8] hover:bg-white/5 disabled:opacity-30"
                            data-testid="clients-next-page"
                          >
                            Suivant →
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Client Detail Modal */}
        <AnimatePresence>
          {clientDetail && (
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setClientDetail(null)} />
              <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="client-detail-modal">
                {clientDetailLoading || clientDetail.loading ? (
                  <p className="text-center text-[#B59ED1] py-8">Chargement...</p>
                ) : (
                  <>
                    <div className="flex items-center gap-4 mb-5">
                      {clientDetail.user?.picture && <img src={clientDetail.user.picture} alt="" className="w-14 h-14 rounded-full" />}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-serif text-2xl" data-testid="client-detail-name">{clientDetail.user?.name}</h3>
                          <button
                            onClick={openClientEdit}
                            className="text-[#B59ED1] hover:text-[#00E5FF] p-1 rounded transition-colors"
                            title="Modifier prénom, nom et téléphone"
                            data-testid="client-edit-btn"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-sm text-[#B59ED1]">{clientDetail.user?.email}</p>
                        {clientDetail.user?.phone && <p className="text-xs text-[#B59ED1]" data-testid="client-detail-phone"><Phone className="w-3 h-3 inline mr-1" />{clientDetail.user.phone}</p>}
                      </div>
                      <button onClick={() => setClientDetail(null)} className="text-[#B59ED1] hover:text-white"><X className="w-5 h-5" /></button>
                    </div>

                    {/* Formulaire édition inline (prénom / nom / téléphone) */}
                    {clientEditOpen && (
                      <div className="mb-5 p-4 bg-[#1B132B] border border-[#00E5FF]/25 rounded-lg space-y-3" data-testid="client-edit-form">
                        <p className="text-xs uppercase tracking-wider text-[#00E5FF]">Modifier l'identité du client</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] uppercase text-[#B59ED1] block mb-1">Prénom</label>
                            <Input
                              value={clientEditForm.first_name}
                              onChange={(e) => setClientEditForm({ ...clientEditForm, first_name: e.target.value })}
                              className="bg-[#261B3B] border-white/10 text-sm"
                              maxLength={60}
                              data-testid="client-edit-firstname"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase text-[#B59ED1] block mb-1">Nom</label>
                            <Input
                              value={clientEditForm.last_name}
                              onChange={(e) => setClientEditForm({ ...clientEditForm, last_name: e.target.value })}
                              className="bg-[#261B3B] border-white/10 text-sm"
                              maxLength={60}
                              data-testid="client-edit-lastname"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase text-[#B59ED1] block mb-1">Téléphone (facultatif)</label>
                          <Input
                            value={clientEditForm.phone}
                            onChange={(e) => setClientEditForm({ ...clientEditForm, phone: e.target.value })}
                            placeholder="+33 6 12 34 56 78"
                            className="bg-[#261B3B] border-white/10 text-sm"
                            data-testid="client-edit-phone"
                          />
                        </div>
                        <div className="flex gap-2 justify-end pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setClientEditOpen(false)}
                            disabled={clientEditSaving}
                            className="border-white/15 text-[#B59ED1] hover:text-white"
                            data-testid="client-edit-cancel"
                          >
                            Annuler
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveClientProfile}
                            disabled={clientEditSaving}
                            className="gradient-primary"
                            data-testid="client-edit-save"
                          >
                            {clientEditSaving ? "Enregistrement…" : "Enregistrer"}
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-3 mb-5">
                      <div className="p-3 bg-[#1B132B] rounded-lg text-center">
                        <p className="text-2xl font-serif text-[#00E5FF]">{clientDetail.appointment_count}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#B59ED1]">RDV</p>
                      </div>
                      <div className="p-3 bg-[#1B132B] rounded-lg text-center">
                        <p className="text-2xl font-serif text-[#D926A9]">{clientDetail.order_count}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#B59ED1]">Commandes</p>
                      </div>
                      <div className="p-3 bg-[#1B132B] rounded-lg text-center">
                        <p className="text-2xl font-serif text-green-400">{clientDetail.total_spent} €</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#B59ED1]">Total</p>
                      </div>
                    </div>

                    {/* Adresse client (livraisons & contact) */}
                    {(clientDetail.user?.address_line_1 || clientDetail.user?.postal_code || clientDetail.user?.city) && (
                      <div className="mb-5 p-3 bg-[#1B132B] border border-white/5 rounded-lg" data-testid="client-address-block">
                        <p className="text-[10px] uppercase tracking-wider text-[#B59ED1] mb-2 flex items-center gap-1.5">
                          <MapPin className="w-3 h-3" /> Adresse
                        </p>
                        <div className="text-sm text-[#E2D5F8] leading-relaxed">
                          {clientDetail.user.address_line_1 && <div>{clientDetail.user.address_line_1}</div>}
                          {clientDetail.user.address_line_2 && <div>{clientDetail.user.address_line_2}</div>}
                          {(clientDetail.user.postal_code || clientDetail.user.city) && (
                            <div>{clientDetail.user.postal_code} {clientDetail.user.city}</div>
                          )}
                          {clientDetail.user.country && <div className="text-xs text-[#B59ED1]">{clientDetail.user.country}</div>}
                        </div>
                        {(clientDetail.user.address_line_1 || clientDetail.user.city) && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([clientDetail.user.address_line_1, clientDetail.user.postal_code, clientDetail.user.city].filter(Boolean).join(' '))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#00E5FF] hover:underline mt-2"
                          >
                            Voir sur Google Maps →
                          </a>
                        )}
                      </div>
                    )}

                    <div className="mb-5">
                      <label className="text-xs text-[#B59ED1] mb-2 block flex items-center gap-2"><Info className="w-3 h-3" />Note admin privée (jamais visible par le client)</label>
                      <Textarea value={clientNotes} onChange={(e) => setClientNotes(e.target.value)} placeholder="ex. Préfère les RDV en visio, allergique aux encens..." className="bg-[#1B132B] border-white/10 min-h-[70px]" data-testid="client-admin-notes" />
                      <Button onClick={saveClientNotes} size="sm" className="gradient-primary mt-2" data-testid="save-client-notes">Enregistrer la note</Button>
                    </div>

                    <div className="mb-4">
                      <h4 className="font-serif text-base mb-2">Rendez-vous ({clientDetail.appointment_count})</h4>
                      {clientDetail.appointments?.length === 0 ? <p className="text-xs text-[#B59ED1]">Aucun RDV</p> :
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {clientDetail.appointments?.slice(0, 10).map(a => (
                          <div key={a.appointment_id} className="p-2.5 bg-[#1B132B] rounded text-xs flex items-center gap-3">
                            <Calendar className="w-3 h-3 text-[#9D4CDD]" />
                            <span className="text-[#E2D5F8]">{a.date} {a.time_slot}</span>
                            <span className="text-[#B59ED1]">{a.service_name}</span>
                            <span className="ml-auto text-[#00E5FF]">{a.price}€</span>
                            <Badge className={`text-[9px] ${a.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : a.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{a.status}</Badge>
                          </div>
                        ))}
                      </div>}
                    </div>

                    <div className="mb-4">
                      <h4 className="font-serif text-base mb-2">Événements ({clientDetail.event_count || 0})</h4>
                      {(!clientDetail.event_registrations || clientDetail.event_registrations.length === 0) ? <p className="text-xs text-[#B59ED1]">Aucune inscription événement</p> :
                      <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="client-detail-events">
                        {clientDetail.event_registrations.slice(0, 10).map(ev => (
                          <div key={ev.registration_id || ev.event_id} className="p-2.5 bg-[#1B132B] rounded text-xs flex items-center gap-3" data-testid={`client-event-${ev.event_id}`}>
                            <Sparkles className="w-3 h-3 text-[#D926A9]" />
                            <span className="text-[#E2D5F8]">{ev.date}{ev.time ? ' ' + ev.time : ''}</span>
                            <span className="text-[#B59ED1] truncate">{ev.event_name}</span>
                            <span className="ml-auto text-[#00E5FF]">{ev.paid ? (ev.amount_paid || ev.price) : ev.price}€</span>
                            <Badge className={`text-[9px] ${ev.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : ev.paid ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{ev.status === 'cancelled' ? 'annulé' : ev.paid ? 'payé' : 'en attente'}</Badge>
                          </div>
                        ))}
                      </div>}
                    </div>

                    <div>
                      <h4 className="font-serif text-base mb-2">Commandes ({clientDetail.order_count})</h4>
                      {clientDetail.orders?.length === 0 ? <p className="text-xs text-[#B59ED1]">Aucune commande</p> :
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {clientDetail.orders?.slice(0, 10).map(o => (
                          <div key={o.order_id} className="p-2.5 bg-[#1B132B] rounded text-xs flex items-center gap-3">
                            <Package className="w-3 h-3 text-[#D926A9]" />
                            <span className="text-[#E2D5F8]">{o.order_number || o.order_id}</span>
                            <span className="text-[#B59ED1]">{o.items?.length || 0} article(s)</span>
                            <span className="ml-auto text-[#00E5FF]">{o.total}€</span>
                            <Badge className={`text-[9px] ${o.status === 'shipped' || o.status === 'delivered' ? 'bg-green-500/20 text-green-400' : o.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{o.status}</Badge>
                          </div>
                        ))}
                      </div>}
                    </div>

                    {/* Retraites & séjours du client */}
                    <div className="mt-4">
                      <h4 className="font-serif text-base mb-2">Séjours & Retraites ({clientDetail.retreat_count || 0})</h4>
                      {(!clientDetail.retreat_bookings || clientDetail.retreat_bookings.length === 0) ? <p className="text-xs text-[#B59ED1]">Aucune retraite réservée</p> :
                      <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="client-detail-retreats">
                        {clientDetail.retreat_bookings.slice(0, 10).map(rb => (
                          <div key={rb.booking_id} className="p-2.5 bg-[#1B132B] rounded text-xs flex items-center gap-3" data-testid={`client-retreat-${rb.booking_id}`}>
                            <Sparkles className="w-3 h-3 text-[#F5D8A2]" />
                            <span className="text-[#E2D5F8]">{rb.start_date}</span>
                            <span className="text-[#B59ED1] truncate">
                              {rb.retreat_title}
                              {rb.booking_type === 'day' ? ` · Day×${rb.day_pax || 1}` : ` · ${rb.room_name || 'Chambre'} (${rb.occupancy === 'double' ? 'double' : 'simple'})`}
                            </span>
                            <span className="ml-auto text-[#00E5FF]">{rb.amount_paid}€/{rb.total_amount}€</span>
                            <Badge className={`text-[9px] ${rb.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : rb.status === 'paid' ? 'bg-green-500/20 text-green-400' : rb.status === 'balance_due' ? 'bg-orange-500/20 text-orange-300' : 'bg-blue-500/20 text-blue-300'}`}>{rb.status}</Badge>
                          </div>
                        ))}
                      </div>}
                    </div>

                    {/* Danger zone — suppression définitive (admin only, jamais sur un autre admin) */}
                    {clientDetail.user?.role !== 'admin' && clientDetail.user?.user_id !== user?.user_id && (
                      <div className="mt-6 pt-5 border-t border-red-500/15">
                        <h4 className="text-xs uppercase tracking-wider text-red-400 mb-2">Zone sensible</h4>
                        <p className="text-xs text-[#B59ED1] mb-3">
                          Supprime définitivement ce compte client. L'historique des commandes et RDV est conservé pour ta comptabilité.
                        </p>
                        <Button
                          onClick={deleteClient}
                          variant="outline"
                          size="sm"
                          className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid="delete-client-btn"
                        >
                          <X className="w-4 h-4 mr-1.5" />
                          Supprimer ce compte
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Orders (shared) */}
        {(!isAdmin || adminTab === 'orders') && (
          <Card className="bg-[#261B3B] border-white/10 mt-6">
            <CardHeader>
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-[#00E5FF]" />
                {isAdmin ? `Commandes (${orders.length})` : 'Mes Commandes'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? <p className="text-[#B59ED1] text-center py-6 text-sm">Aucune commande</p>
              : <div className="space-y-3">{(isAdmin
                  ? [...orders].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                  : orders.slice(0, 10)
                ).map(order => {
                  const deliveryLabel = {
                    click_collect: 'Click & Collect',
                    mondial_relay_point: 'Mondial Relay - Point',
                    mondial_relay_home: 'Livraison à domicile'
                  }[order.delivery_method] || order.delivery_method;
                  const statusBadge = {
                    pending: { label: 'En attente', cls: 'bg-yellow-500/20 text-yellow-400' },
                    paid: { label: 'Payée', cls: 'bg-green-500/20 text-green-400' },
                    ready: { label: 'Prête à retirer', cls: 'bg-[#00E5FF]/20 text-[#00E5FF]' },
                    shipped: { label: 'Expédiée', cls: 'bg-blue-500/20 text-blue-400' },
                    delivered: { label: 'Livrée', cls: 'bg-green-500/20 text-green-400' },
                    picked_up: { label: 'Récupérée', cls: 'bg-green-500/20 text-green-400' },
                    cancelled: { label: 'Annulée', cls: 'bg-red-500/20 text-red-400' }
                  }[order.status] || { label: order.status, cls: 'bg-white/10 text-[#B59ED1]' };
                  const isMondial = (order.delivery_method || '').includes('mondial_relay');
                  return (
                    <div key={order.order_id} className="p-4 bg-[#1B132B] rounded-lg" data-testid={`order-${order.order_id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-[#B59ED1] text-xs font-mono">#{order.order_id.slice(-8)}</span>
                          <Badge className={`text-[10px] ${statusBadge.cls}`}>{statusBadge.label}</Badge>
                          {order.created_at && (
                            <span className="text-xs text-white font-medium" data-testid={`order-date-${order.order_id}`}>
                              📅 {new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              {' '}à {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <span className="font-serif text-lg text-[#D926A9]">{order.total?.toFixed(2)} €</span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-[#B59ED1] flex-wrap mb-3">
                        <span>{order.items?.length || 0} article{(order.items?.length || 0) > 1 ? 's' : ''}</span>
                        {order.delivery_method && (
                          <Badge className={`text-[10px] ${order.delivery_method === 'click_collect' ? 'bg-[#00E5FF]/10 text-[#00E5FF]' : 'bg-[#D926A9]/10 text-[#D926A9]'}`}>
                            {deliveryLabel}
                          </Badge>
                        )}
                      </div>

                      {/* Bloc client (admin uniquement) — toujours visible pour expédition */}
                      {isAdmin && (order.customer_name || order.customer_email || order.customer_phone) && (
                        <div className="mb-3 p-3 bg-white/5 border border-white/10 rounded">
                          <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1.5">Coordonnées client</p>
                          {order.customer_name && <p className="text-sm text-white font-medium">{order.customer_name}</p>}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                            {order.customer_email && (
                              <a href={`mailto:${order.customer_email}`} className="text-[#00E5FF] hover:underline">✉️ {order.customer_email}</a>
                            )}
                            {order.customer_phone && (
                              <a href={`tel:${order.customer_phone}`} className="text-[#00E5FF] hover:underline">📞 {order.customer_phone}</a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Bloc Click & Collect : date + heure de retrait */}
                      {order.delivery_method === 'click_collect' && order.pickup_date && (
                        <div className="mb-3 p-3 bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded">
                          <p className="text-[10px] text-[#00E5FF] uppercase tracking-wider mb-1">Retrait en boutique</p>
                          <p className="text-sm text-white">
                            {new Date(order.pickup_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            {order.pickup_time_slot && ` à ${order.pickup_time_slot}`}
                          </p>
                          <p className="text-[11px] text-[#B59ED1] mt-1">20 Quai du Roubion, 26200 Montélimar</p>
                        </div>
                      )}

                      {/* Bloc Mondial Relay Domicile : adresse de livraison */}
                      {isAdmin && order.delivery_method === 'mondial_relay_home' && order.shipping_address && (
                        <div className="mb-3 p-3 bg-[#D926A9]/5 border border-[#D926A9]/20 rounded">
                          <p className="text-[10px] text-[#D926A9] uppercase tracking-wider mb-1">Livraison à domicile</p>
                          <p className="text-sm text-white">{order.shipping_address.name || order.customer_name}</p>
                          <p className="text-xs text-[#E2D5F8]">
                            {order.shipping_address.address_line_1 || order.shipping_address.address || order.shipping_address.street || ''}
                          </p>
                          {(order.shipping_address.address_line_2) && (
                            <p className="text-xs text-[#E2D5F8]">{order.shipping_address.address_line_2}</p>
                          )}
                          <p className="text-xs text-[#E2D5F8]">
                            {order.shipping_address.postal_code || order.shipping_address.zip || ''}{' '}
                            {order.shipping_address.city || ''}
                          </p>
                          {order.shipping_address.country && <p className="text-xs text-[#E2D5F8]">{order.shipping_address.country}</p>}
                        </div>
                      )}

                      {/* Bloc Mondial Relay Point relais — supporte les 2 formats (Sendcloud + Mondial Relay) */}
                      {isAdmin && order.delivery_method === 'mondial_relay_point' && (order.point_relais || order.relay_point) && (() => {
                        const pr = order.point_relais || order.relay_point;
                        const prName = pr.name || pr.Nom || 'Point relais';
                        const prAddr = pr.street || pr.Adresse1 || pr.address || '';
                        const prAddr2 = pr.house_number || pr.Adresse2 || '';
                        const prCP = pr.postal_code || pr.CP || pr.zip || '';
                        const prCity = pr.city || pr.Ville || '';
                        const prCountry = pr.country || pr.Pays || '';
                        const prId = pr.id || pr.ID || order.relay_point_id;
                        return (
                          <div className="mb-3 p-3 bg-[#D926A9]/5 border border-[#D926A9]/20 rounded">
                            <p className="text-[10px] text-[#D926A9] uppercase tracking-wider mb-1">Point relais Mondial Relay</p>
                            <p className="text-sm text-white font-medium">{prName}</p>
                            {prAddr && <p className="text-xs text-[#E2D5F8]">{prAddr} {prAddr2}</p>}
                            {(prCP || prCity) && <p className="text-xs text-[#E2D5F8]">{prCP} {prCity} {prCountry && `(${prCountry})`}</p>}
                            {prId && <p className="text-[10px] text-[#B59ED1] mt-1 font-mono">ID Mondial Relay : {prId}</p>}
                          </div>
                        );
                      })()}

                      {/* Numero de suivi si la commande est expediee */}
                      {order.tracking_number && (
                        <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                          <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-1">Numéro de suivi</p>
                          <p className="text-sm text-white font-mono tracking-wider">{order.tracking_number}</p>
                          {order.tracking_url ? (
                            <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-[#00E5FF] hover:underline mt-1 inline-block">Voir le suivi →</a>
                          ) : isMondial ? (
                            <a href={`https://www.mondialrelay.fr/suivi-de-colis?numeroExpedition=${order.tracking_number}`} target="_blank" rel="noreferrer" className="text-xs text-[#00E5FF] hover:underline mt-1 inline-block">Suivre sur Mondial Relay →</a>
                          ) : null}
                        </div>
                      )}

                      {/* Téléchargement facture (commandes payées uniquement) */}
                      {['paid', 'ready', 'shipped', 'delivered', 'picked_up'].includes(order.status) && (
                        <div className="mb-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 text-xs"
                            data-testid={`order-invoice-${order.order_id}`}
                            onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/orders/${order.order_id}/invoice`, {
                                  withCredentials: true,
                                  responseType: 'blob',
                                });
                                const blob = new Blob([res.data], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = (order.invoice_number || `facture_${order.order_id}`) + '.pdf';
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                window.URL.revokeObjectURL(url);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Impossible de télécharger la facture");
                              }
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Télécharger ma facture {order.invoice_number ? `(${order.invoice_number})` : ''}
                          </Button>
                        </div>
                      )}

                      {/* Detail des articles */}
                      {order.items && order.items.length > 0 && (
                        <details className="mb-3" open={isAdmin}>
                          <summary className="text-xs text-[#B59ED1] cursor-pointer hover:text-white">
                            Détail des produits ({order.items.length})
                          </summary>
                          <ul className="mt-2 space-y-2 text-xs">
                            {order.items.map((it, idx) => (
                              <li key={it.sku || `${it.name}-${idx}`} className="flex justify-between items-start gap-3 p-2 bg-[#261B3B]/50 rounded">
                                <div className="flex-1">
                                  <p className="text-[#E2D5F8]">{it.quantity} × {it.name}</p>
                                  {it.variation_name && <p className="text-[10px] text-[#B59ED1] mt-0.5">Variante : {it.variation_name}</p>}
                                  {it.sku && <p className="text-[10px] text-[#B59ED1]/70 mt-0.5 font-mono">SKU : {it.sku}</p>}
                                </div>
                                <span className="text-white whitespace-nowrap">{(it.subtotal ?? it.price * it.quantity).toFixed(2)} €</span>
                              </li>
                            ))}
                          </ul>
                          {order.notes && <p className="mt-2 text-xs text-[#E2D5F8] italic bg-[#261B3B]/50 p-2 rounded">Note : {order.notes}</p>}
                        </details>
                      )}

                      {/* Bouton "Régler ma commande" (client uniquement, statut pending_payment ou pending) */}
                      {!isAdmin && (order.status === 'pending_payment' || order.status === 'pending') && (
                        <Button
                          className="gradient-primary w-full mt-3"
                          onClick={() => setPayModal({
                            kind: 'order',
                            id: order.order_id,
                            amount: order.total,
                            title: `Commande #${order.order_id.slice(-8)} — ${(order.items || []).length} article(s)`,
                          })}
                          data-testid={`pay-order-${order.order_id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" /> Régler ma commande
                        </Button>
                      )}

                      {/* Boutons d'actions admin */}
                      {isAdmin && (
                        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/5">
                          {order.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.order_id, 'paid')} className="border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs" data-testid={`order-mark-paid-${order.order_id}`}>
                              <Check className="w-3 h-3 mr-1" />Marquer payée
                            </Button>
                          )}
                          {(order.status === 'pending' || order.status === 'paid') && order.delivery_method === 'click_collect' && (
                            <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.order_id, 'ready')} className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 text-xs" data-testid={`order-mark-ready-${order.order_id}`}>
                              Prête à retirer
                            </Button>
                          )}
                          {order.status === 'ready' && order.delivery_method === 'click_collect' && (
                            <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.order_id, 'picked_up')} className="border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs" data-testid={`order-mark-pickedup-${order.order_id}`}>
                              <Check className="w-3 h-3 mr-1" />Récupérée
                            </Button>
                          )}
                          {(order.status === 'pending' || order.status === 'paid') && isMondial && (
                            <Button size="sm" className="gradient-primary text-xs" onClick={() => setShipModal({ order })} data-testid={`order-ship-${order.order_id}`}>
                              Marquer expédiée (+ suivi)
                            </Button>
                          )}
                          {order.status === 'shipped' && (
                            <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.order_id, 'delivered')} className="border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs" data-testid={`order-mark-delivered-${order.order_id}`}>
                              <Check className="w-3 h-3 mr-1" />Livrée
                            </Button>
                          )}
                          {order.status !== 'cancelled' && order.status !== 'picked_up' && order.status !== 'delivered' && (
                            <Button size="sm" variant="outline" onClick={() => { if (window.confirm('Annuler cette commande ?')) updateOrderStatus(order.order_id, 'cancelled'); }} className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs ml-auto" data-testid={`order-cancel-${order.order_id}`}>
                              Annuler
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}</div>}
              {!isAdmin && <Button onClick={() => navigate('/boutique')} variant="outline" className="w-full mt-4 border-[#00E5FF]/40 text-[#00E5FF]" data-testid="shop-more">Continuer mes achats</Button>}
            </CardContent>
          </Card>
        )}

        {/* ADMIN: Push Notifications */}
        {isAdmin && adminTab === 'notifications' && <AdminPushBroadcast />}

        {/* CLIENT: Mes Retraites */}
        {!isAdmin && <MyRetreatsSection />}

        {/* ADMIN: Newsletter (Brevo List) */}
        {isAdmin && adminTab === 'newsletter' && <AdminNewsletterManager />}
      </div>

      {/* Ship Order Modal (admin : saisie du numero de suivi Mondial Relay) */}
      <AnimatePresence>
        {shipModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShipModal(null)} />
            <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-md w-full" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="ship-modal">
              <h3 className="font-serif text-2xl mb-2">Marquer comme expédiée</h3>
              <p className="text-xs text-[#B59ED1] mb-4">
                Commande <span className="font-mono text-white">#{shipModal.order.order_id.slice(-8)}</span> &mdash; {shipModal.order.customer_name}
              </p>

              <div className="space-y-3 mb-5">
                {/* Workflow manuel Sendcloud (API v2 désactivée pour les comptes Free) */}
                {(shipModal.order.delivery_method === 'mondial_relay_point' || shipModal.order.delivery_method === 'mondial_relay_home' || shipModal.order.delivery_method === 'colissimo' || shipModal.order.delivery_method === 'home_delivery') && (
                  <div className="p-3 rounded-lg border border-[#D926A9]/30 bg-[#D926A9]/10 space-y-2" data-testid="sendcloud-manual-info">
                    <p className="text-xs text-[#F7F3FC] font-medium">📦 Génération d'étiquette</p>
                    <p className="text-[11px] text-[#B59ED1] leading-relaxed">
                      Crée l'expédition manuellement sur le panel Sendcloud, puis copie le numéro de suivi ci-dessous.
                    </p>
                    <a
                      href="https://app.sendcloud.com/v2/shipping/orders/new"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-[#00E5FF] hover:underline"
                      data-testid="sendcloud-panel-link"
                    >
                      → Ouvrir Sendcloud pour générer l'étiquette
                    </a>
                  </div>
                )}

                <div>
                  <Label className="text-xs text-[#B59ED1]">Numéro de suivi *</Label>
                  <Input
                    value={shipTracking}
                    onChange={e => setShipTracking(e.target.value)}
                    placeholder="ex : 82234567"
                    className="mt-1 bg-[#1B132B] border-white/10 font-mono"
                    data-testid="ship-tracking-number"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#B59ED1]">Lien de suivi (optionnel)</Label>
                  <Input
                    value={shipUrl}
                    onChange={e => setShipUrl(e.target.value)}
                    placeholder="https://... (sinon généré automatiquement)"
                    className="mt-1 bg-[#1B132B] border-white/10 text-xs"
                    data-testid="ship-tracking-url"
                  />
                  <p className="text-[10px] text-[#B59ED1]/70 mt-1">Laisser vide pour générer automatiquement un lien Mondial Relay à partir du numéro.</p>
                </div>
                <div className="p-3 bg-[#00E5FF]/10 border border-[#00E5FF]/20 rounded text-xs text-[#E2D5F8]">
                  📧 Un email sera automatiquement envoyé à <span className="text-[#00E5FF]">{shipModal.order.customer_email || 'le client'}</span> avec le numéro de suivi.
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="gradient-primary flex-1" onClick={handleShipOrder} data-testid="ship-submit">
                  <Check className="w-4 h-4 mr-2" />Confirmer et envoyer l&apos;email
                </Button>
                <Button variant="outline" onClick={() => setShipModal(null)} className="border-white/10">Annuler</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Appointment Modal (admin) */}
      <AnimatePresence>
        {newAptModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setNewAptModal(null)} />
            <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="new-apt-modal">
              <h3 className="font-serif text-2xl mb-4">Nouveau rendez-vous</h3>

              <div className="space-y-3">
                {/* Service */}
                <div>
                  <Label className="text-xs text-[#B59ED1]">Prestation *</Label>
                  <select
                    value={newApt.service_id}
                    onChange={e => setNewApt({ ...newApt, service_id: e.target.value, variation_id: '' })}
                    className="w-full mt-1 px-3 py-2 bg-[#1B132B] border border-white/10 rounded-lg text-sm text-white"
                    data-testid="new-apt-service"
                  >
                    <option value="">Choisis une prestation...</option>
                    {services.map(s => (
                      <option key={s.service_id} value={s.service_id}>{s.name} — {s.price}€ ({s.duration_minutes} min)</option>
                    ))}
                  </select>
                </div>

                {/* Variation (si presente) */}
                {(() => {
                  const svc = services.find(s => s.service_id === newApt.service_id);
                  const vars = svc?.variations || [];
                  if (vars.length === 0) return null;
                  return (
                    <div>
                      <Label className="text-xs text-[#B59ED1]">Variante</Label>
                      <select
                        value={newApt.variation_id}
                        onChange={e => setNewApt({ ...newApt, variation_id: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-[#1B132B] border border-white/10 rounded-lg text-sm text-white"
                        data-testid="new-apt-variation"
                      >
                        <option value="">Tarif standard</option>
                        {vars.map(v => (
                          <option key={v.variation_id} value={v.variation_id}>{v.name} — {v.price}€</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                <div>
                  <Label className="text-xs text-[#B59ED1]">Date *</Label>
                  <Input type="date" value={newApt.date} onChange={e => setNewApt({...newApt, date: e.target.value, time_slot: ''})} className="mt-1 bg-[#1B132B] border-white/10" data-testid="new-apt-date" />
                </div>

                {/* Creneaux intelligents */}
                <div>
                  <Label className="text-xs text-[#B59ED1]">Heure *</Label>
                  {!newApt.service_id ? (
                    <p className="text-xs text-[#B59ED1]/60 mt-1 italic">Choisis d&apos;abord une prestation pour voir les créneaux libres</p>
                  ) : newAptLoadingSlots ? (
                    <p className="text-xs text-[#B59ED1] mt-2">Chargement des créneaux...</p>
                  ) : newAptAvailableSlots.length === 0 ? (
                    <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                      Aucun créneau disponible pour cette date.
                      <Input type="time" value={newApt.time_slot} onChange={e => setNewApt({...newApt, time_slot: e.target.value})} placeholder="Forcer une heure" className="mt-2 bg-[#1B132B] border-white/10" data-testid="new-apt-time-force" />
                      <p className="text-[10px] text-[#B59ED1] mt-1">Tu peux forcer un horaire (usage exceptionnel).</p>
                    </div>
                  ) : (
                    <div className="mt-2 grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-56 overflow-y-auto p-2 bg-[#1B132B] rounded-lg border border-white/5">
                      {newAptAvailableSlots.map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setNewApt({...newApt, time_slot: s})}
                          className={`py-2 rounded text-xs font-medium transition-all ${newApt.time_slot === s ? 'bg-[#D926A9] text-white' : 'bg-[#261B3B] text-[#B59ED1] hover:bg-[#3A115E] hover:text-white'}`}
                          data-testid={`new-apt-slot-${s}`}
                        >{s}</button>
                      ))}
                    </div>
                  )}
                  {newApt.time_slot && (
                    <p className="text-[10px] text-[#00E5FF] mt-1">✓ Créneau sélectionné : {newApt.time_slot}</p>
                  )}
                </div>

                {/* Mode */}
                <div>
                  <Label className="text-xs text-[#B59ED1]">Mode</Label>
                  <div className="flex gap-2 mt-1">
                    {[
                      { v: 'presentiel', label: 'En boutique' },
                      { v: 'distance', label: 'À distance' }
                    ].map(m => (
                      <button
                        key={m.v}
                        onClick={() => setNewApt({...newApt, location_mode: m.v})}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${newApt.location_mode === m.v ? 'bg-[#D926A9] text-white' : 'bg-[#1B132B] border border-white/10 text-[#B59ED1] hover:text-white'}`}
                        data-testid={`new-apt-mode-${m.v}`}
                      >{m.label}</button>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-white/10 my-2" />

                {/* Sélecteur client (recherche dans la base + création rapide) */}
                <ClientPicker
                  clients={clients}
                  selected={newApt._selectedClient || null}
                  onSelect={(c) => {
                    if (c) {
                      setNewApt({
                        ...newApt,
                        _selectedClient: c,
                        customer_name: c.name || '',
                        customer_email: c.email || '',
                        customer_phone: c.phone || '',
                      });
                    } else {
                      setNewApt({ ...newApt, _selectedClient: null, customer_name: '', customer_email: '', customer_phone: '' });
                    }
                  }}
                  onCreated={(c) => { setClients(prev => [c, ...prev.filter(x => x.user_id !== c.user_id)]); }}
                  placeholder="Rechercher par nom, email ou téléphone…"
                  dataTestidPrefix="new-apt-client"
                />

                {/* Fallback : édition manuelle (utile si le client n'a pas de compte) */}
                {!newApt._selectedClient && (
                  <>
                    <div>
                      <Label className="text-xs text-[#B59ED1]">Nom du client *</Label>
                      <Input value={newApt.customer_name} onChange={e => setNewApt({...newApt, customer_name: e.target.value})} placeholder="Nom Prénom" className="mt-1 bg-[#1B132B] border-white/10" data-testid="new-apt-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#B59ED1]">Email</Label>
                        <Input type="email" value={newApt.customer_email} onChange={e => setNewApt({...newApt, customer_email: e.target.value})} placeholder="client@email.com" className="mt-1 bg-[#1B132B] border-white/10" data-testid="new-apt-email" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#B59ED1]">Téléphone</Label>
                        <Input value={newApt.customer_phone} onChange={e => setNewApt({...newApt, customer_phone: e.target.value})} placeholder="06 00 00 00 00" className="mt-1 bg-[#1B132B] border-white/10" data-testid="new-apt-phone" />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <Label className="text-xs text-[#B59ED1]">Paiement</Label>
                  <select
                    value={newApt.payment_method}
                    onChange={e => setNewApt({...newApt, payment_method: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-[#1B132B] border border-white/10 rounded-lg text-sm text-white"
                    data-testid="new-apt-payment"
                  >
                    <option value="pay_at_shop">À régler en boutique</option>
                    <option value="online">À payer en ligne</option>
                    <option value="paid">Déjà payé (hors Square)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs text-[#B59ED1]">Note (optionnelle)</Label>
                  <Input value={newApt.notes} onChange={e => setNewApt({...newApt, notes: e.target.value})} placeholder="Remarques..." className="mt-1 bg-[#1B132B] border-white/10" data-testid="new-apt-notes" />
                </div>

                <p className="text-[10px] text-[#B59ED1] italic">
                  Astuce : la création admin contourne les créneaux occupés. Vérifie bien qu&apos;il n&apos;y a pas de conflit.
                </p>
              </div>

              <div className="flex gap-2 mt-5">
                <Button className="gradient-primary flex-1" onClick={handleCreateAdminApt} data-testid="new-apt-submit">
                  <Check className="w-4 h-4 mr-2" />Créer le rendez-vous
                </Button>
                <Button variant="outline" onClick={() => setNewAptModal(null)} className="border-white/10">Annuler</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Appointment Detail Modal (admin click on calendar) */}
      <AnimatePresence>
        {aptDetailModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAptDetailModal(null)} />
            <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-lg w-full" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="apt-detail-modal">
              {/* Entete : titre + status + prix */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-serif text-xl mb-1">{aptDetailModal.service_name || aptDetailModal.service?.name}</h3>
                  {aptDetailModal.variation_name && (
                    <p className="text-xs text-[#B59ED1]">{aptDetailModal.variation_name}</p>
                  )}
                </div>
                <span className="text-[#D926A9] font-serif text-2xl">{aptDetailModal.price} €</span>
              </div>

              {/* Informations RDV */}
              <div className="space-y-3 mb-5">
                <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5">
                  <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Quand</p>
                  <p className="text-sm text-white capitalize">
                    {new Date(aptDetailModal.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <p className="text-sm text-[#D926A9]">{aptDetailModal.time_slot} — {aptDetailModal.end_time}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5">
                    <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Mode</p>
                    <Badge className={`text-[10px] ${aptDetailModal.location_mode === 'distance' ? 'bg-[#00E5FF]/20 text-[#00E5FF]' : 'bg-[#D926A9]/20 text-[#D926A9]'}`}>
                      {aptDetailModal.location_mode === 'distance' ? 'À distance' : 'En boutique'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5">
                    <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Paiement</p>
                    <p className="text-xs text-white">{paymentLabel(aptDetailModal.payment_status)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5">
                    <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Statut</p>
                    <Badge className={`text-[10px] ${statusColor(aptDetailModal.status)}`}>{statusLabel(aptDetailModal.status)}</Badge>
                  </div>
                  <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5">
                    <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Durée</p>
                    <p className="text-xs text-white">{(() => {
                      if (aptDetailModal.duration_minutes) return `${aptDetailModal.duration_minutes} min`;
                      if (aptDetailModal.time_slot && aptDetailModal.end_time) {
                        const [sh, sm] = aptDetailModal.time_slot.split(':').map(Number);
                        const [eh, em] = aptDetailModal.end_time.split(':').map(Number);
                        const d = (eh * 60 + em) - (sh * 60 + sm);
                        if (d > 0) return `${d} min`;
                      }
                      return '—';
                    })()}</p>
                  </div>
                </div>

                {/* Client info (admin) */}
                {isAdmin && (aptDetailModal.customer_name || aptDetailModal.customer_email) && (
                  <div className="p-3 bg-[#1B132B] rounded-lg border border-[#D926A9]/20">
                    <p className="text-[10px] text-[#D926A9] uppercase tracking-wider mb-1">Client</p>
                    {aptDetailModal.customer_name && <p className="text-sm text-white">{aptDetailModal.customer_name}</p>}
                    {aptDetailModal.customer_email && (
                      <a href={`mailto:${aptDetailModal.customer_email}`} className="text-xs text-[#00E5FF] hover:underline">
                        {aptDetailModal.customer_email}
                      </a>
                    )}
                    {aptDetailModal.customer_phone && (
                      <p className="text-xs text-[#B59ED1] mt-0.5">
                        <a href={`tel:${aptDetailModal.customer_phone}`} className="hover:text-[#00E5FF]">{aptDetailModal.customer_phone}</a>
                      </p>
                    )}
                  </div>
                )}

                {aptDetailModal.notes && (
                  <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5">
                    <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Note</p>
                    <p className="text-xs text-[#E2D5F8] italic">{aptDetailModal.notes}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {(canModify(aptDetailModal) || (isAdmin && aptDetailModal.status !== 'completed' && aptDetailModal.status !== 'cancelled')) && (
                  <Button
                    onClick={() => {
                      setRescheduleModal(aptDetailModal);
                      setAptDetailModal(null);
                    }}
                    className="gradient-primary flex-1"
                    data-testid="apt-detail-modify"
                  >
                    <Settings className="w-4 h-4 mr-2" />Modifier
                  </Button>
                )}
                {isAdmin && aptDetailModal.status === 'confirmed' && (
                  <Button
                    variant="outline"
                    onClick={() => { completeAppointment(aptDetailModal.appointment_id); setAptDetailModal(null); }}
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    data-testid="apt-detail-complete"
                  >
                    <Check className="w-4 h-4 mr-2" />Terminer
                  </Button>
                )}
                {/* Admin : Rétablir un RDV coché "terminé" par erreur */}
                {isAdmin && aptDetailModal.status === 'completed' && (
                  <Button
                    variant="outline"
                    onClick={() => { restoreAppointment(aptDetailModal.appointment_id); setAptDetailModal(null); }}
                    className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                    data-testid="apt-detail-restore"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />Rétablir (Confirmé)
                  </Button>
                )}
                {/* Annuler : règle client (canModify) OU admin sur tout RDV non annulé */}
                {(canModify(aptDetailModal) || (isAdmin && canAdminModify(aptDetailModal))) && (
                  <Button
                    variant="outline"
                    onClick={() => { cancelAppointment(aptDetailModal.appointment_id); setAptDetailModal(null); }}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="apt-detail-cancel"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />Annuler
                  </Button>
                )}
                <Button variant="outline" onClick={() => setAptDetailModal(null)} className="border-white/10">Fermer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Modale résumé d'un événement (clic depuis l'agenda) */}
        {eventDetailModal && (() => {
          const ev = eventDetailModal;
          const et = ev.event_type || {};
          const evName = et.name || ev.name || ev.title || 'Événement';
          const allRegs = ev.registrations || [];
          const activeRegs = allRegs.filter(r => r.status !== 'cancelled');
          const cancelledCount = allRegs.length - activeRegs.length;
          const paidRegs = activeRegs.filter(r => r.paid || r.status === 'confirmed');
          const pendingRegs = activeRegs.filter(r => r.status === 'pending_payment' && !r.paid);
          const unitPrice = Number(ev.price ?? et.price ?? 0);
          const revenue = paidRegs.reduce((s, r) => s + Number(r.amount_paid ?? unitPrice ?? 0), 0);
          const dur = et.duration_minutes || 120;
          return (
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEventDetailModal(null)} />
              <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="event-detail-modal">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-amber-300">✨</span>
                      <h3 className="font-serif text-xl">{evName}</h3>
                    </div>
                    {ev.subtitle && <p className="text-xs text-[#B59ED1] italic mt-0.5">{ev.subtitle}</p>}
                  </div>
                  <span className="text-[#D926A9] font-serif text-2xl whitespace-nowrap">{unitPrice > 0 ? `${unitPrice} €` : 'Gratuit'}</span>
                </div>

                <div className="p-3 bg-[#1B132B] rounded-lg border border-white/5 mb-4">
                  <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-1">Quand</p>
                  <p className="text-sm text-white capitalize">{new Date(ev.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</p>
                  <p className="text-sm text-[#D926A9]">{ev.time || '—'} · {dur} min</p>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="p-3 bg-[#1B132B] rounded-lg text-center border border-white/5">
                    <p className="text-2xl font-serif text-[#00E5FF] leading-none" data-testid="evt-detail-count">{activeRegs.length}<span className="text-sm text-[#B59ED1]">/{ev.max_participants}</span></p>
                    <p className="text-[10px] text-[#B59ED1] uppercase mt-1">Inscrits</p>
                  </div>
                  <div className="p-3 bg-[#1B132B] rounded-lg text-center border border-white/5">
                    <p className="text-2xl font-serif text-green-400 leading-none">{paidRegs.length}{pendingRegs.length ? <span className="text-sm text-yellow-400"> +{pendingRegs.length}⏳</span> : null}</p>
                    <p className="text-[10px] text-[#B59ED1] uppercase mt-1">Payés</p>
                  </div>
                  <div className="p-3 bg-[#1B132B] rounded-lg text-center border border-white/5">
                    <p className="text-2xl font-serif text-[#D926A9] leading-none">{revenue % 1 === 0 ? revenue : revenue.toFixed(2)} €</p>
                    <p className="text-[10px] text-[#B59ED1] uppercase mt-1">Encaissé</p>
                  </div>
                </div>

                <div className="mb-5">
                  <p className="text-[10px] text-[#B59ED1] uppercase tracking-wider mb-2">Participants{cancelledCount ? ` (${cancelledCount} annulé·s)` : ''}</p>
                  {activeRegs.length === 0 ? (
                    <p className="text-xs text-[#B59ED1]">Aucun inscrit pour le moment.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-52 overflow-y-auto">
                      {activeRegs.map((r, i) => (
                        <div key={r.registration_id || r.user_id || r.email || i} className="p-2.5 bg-[#1B132B] rounded-lg flex items-center gap-2 text-xs" data-testid={`evt-detail-reg-${i}`}>
                          <span className="flex-1 truncate text-white">{r.name || r.email || '—'}</span>
                          {r.email && <span className="text-[#B59ED1] truncate hidden sm:inline max-w-[140px]">{r.email}</span>}
                          <Badge className={`text-[9px] ${r.paid || r.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {r.paid || r.status === 'confirmed' ? 'Payé' : 'En attente'}
                          </Badge>
                          {r.attended && <Badge className="text-[9px] bg-[#00E5FF]/20 text-[#00E5FF]">Présent·e</Badge>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => { setEventDetailModal(null); setAdminTab('events'); }} className="gradient-primary flex-1" data-testid="event-detail-manage">Gérer les inscrits</Button>
                  <Button variant="outline" onClick={() => setEventDetailModal(null)} className="border-white/10" data-testid="event-detail-close">Fermer</Button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Modale "Payer maintenant" pour RDV/commandes/événements en attente */}
      {payModal && (
        <PayPendingModal
          kind={payModal.kind}
          id={payModal.id}
          amount={payModal.amount}
          title={payModal.title}
          buyer={{ name: user?.name, email: user?.email }}
          onClose={() => setPayModal(null)}
          onPaid={() => { setPayModal(null); window.location.reload(); }}
        />
      )}

      {/* Reschedule Modal — version riche avec calendrier + créneaux */}
      {rescheduleModal && (
        <RescheduleModal
          appointment={rescheduleModal}
          onClose={() => setRescheduleModal(null)}
          onSuccess={() => { setRescheduleModal(null); window.location.reload(); }}
        />
      )}

      {/* Confirmation d'annulation de rendez-vous */}
      <AlertDialog open={!!cancelConfirm} onOpenChange={(open) => { if (!open) setCancelConfirm(null); }}>
        <AlertDialogContent className="bg-[#201435] border-white/10 text-white" data-testid="cancel-appointment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl text-white">Annuler ce rendez-vous ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#B59ED1]">
              Cette action est définitive : le créneau sera libéré et le rendez-vous passera au statut « annulé ». Souhaitez-vous confirmer l'annulation ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="cancel-appointment-dismiss">
              Non, conserver
            </AlertDialogCancel>
            <AlertDialogAction onClick={doCancelAppointment} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-appointment-confirm">
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* CSV Exports Modal (admin backups) */}
      {exportsOpen && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !exportingKind && setExportsOpen(false)} data-testid="csv-exports-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#261B3B] border border-white/10 rounded-2xl max-w-xl w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-serif text-xl text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#00E5FF]" /> Exports CSV — sauvegardes
                </h3>
                <p className="text-xs text-[#B59ED1] mt-1">
                  Télécharge tes données au format CSV (UTF-8 BOM, compatible Excel & Google Sheets).
                </p>
              </div>
              <button onClick={() => !exportingKind && setExportsOpen(false)} className="text-[#B59ED1] hover:text-white p-1" data-testid="csv-exports-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { kind: "clients", label: "Clients", desc: "Tous les utilisateurs avec leurs coordonnées", icon: Users, color: "#00E5FF" },
                { kind: "appointments", label: "Rendez-vous", desc: "Toutes les consultations (passées + à venir)", icon: Calendar, color: "#D926A9" },
                { kind: "orders", label: "Commandes", desc: "Toutes les commandes boutique avec livraison", icon: Package, color: "#9D4CDD" },
                { kind: "events", label: "Événements", desc: "Tous les événements + leurs inscriptions", icon: Sparkles, color: "#FFB400" },
              ].map((ex) => (
                <button
                  key={ex.kind}
                  onClick={() => handleExport(ex.kind)}
                  disabled={!!exportingKind}
                  className="text-left bg-[#1B132B] border border-white/10 rounded-xl p-4 hover:border-[#00E5FF]/40 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`export-${ex.kind}-btn`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ex.color + "20", color: ex.color }}>
                      {exportingKind === ex.kind ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ex.icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{ex.label}</p>
                      <p className="text-xs text-[#B59ED1] mt-1 leading-snug">{ex.desc}</p>
                    </div>
                    <Download className="w-3.5 h-3.5 text-[#B59ED1] flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-5 text-[11px] text-[#B59ED1] bg-[#1B132B]/60 rounded-lg p-3 border border-white/5 leading-relaxed">
              💡 <strong className="text-[#E2D5F8]">Conseil</strong> : sauvegarde tes 4 exports une fois par mois sur un disque externe ou un cloud (Drive, Dropbox…) — c&apos;est ta meilleure assurance en cas d&apos;incident technique majeur.
            </div>

            <Button
              onClick={() => setExportsOpen(false)}
              disabled={!!exportingKind}
              variant="outline"
              className="border-white/10 w-full mt-5"
            >
              Fermer
            </Button>
          </motion.div>
        </div>
      )}

      {/* Wix CSV Import Modal (admin) */}
      {wixImportOpen && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !wixImporting && setWixImportOpen(false)} data-testid="wix-import-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#261B3B] border border-white/10 rounded-2xl max-w-xl w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-serif text-xl text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#D926A9]" /> Importer des contacts (CSV Wix)
                </h3>
                <p className="text-xs text-[#B59ED1] mt-1">
                  Importe un export « Fichier CSV régulier » depuis Wix Contacts.
                </p>
              </div>
              <button onClick={() => !wixImporting && setWixImportOpen(false)} className="text-[#B59ED1] hover:text-white p-1" data-testid="wix-import-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!wixReport ? (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-2 block">Fichier CSV</label>
                    <div className="bg-[#1B132B] border border-dashed border-white/15 rounded-lg p-5 text-center hover:border-[#D926A9]/40 transition-colors">
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => setWixFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="wix-file-input"
                        data-testid="wix-file-input"
                      />
                      <label htmlFor="wix-file-input" className="cursor-pointer block">
                        <FileText className="w-8 h-8 mx-auto text-[#B59ED1] mb-2" />
                        {wixFile ? (
                          <div>
                            <p className="text-sm text-white">{wixFile.name}</p>
                            <p className="text-xs text-[#B59ED1] mt-1">{(wixFile.size / 1024).toFixed(1)} Ko · clique pour changer</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-[#E2D5F8]">Clique pour sélectionner ton fichier .csv</p>
                            <p className="text-xs text-[#B59ED1] mt-1">10 Mo max</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer bg-[#1B132B] p-3 rounded-lg border border-white/10">
                    <input
                      type="checkbox"
                      checked={wixPushBrevo}
                      onChange={(e) => setWixPushBrevo(e.target.checked)}
                      className="mt-1"
                      data-testid="wix-push-brevo"
                    />
                    <span className="text-xs text-[#E2D5F8]">
                      <strong className="text-white">Synchroniser dans Brevo</strong>
                      <span className="block text-[#B59ED1] mt-1">
                        Pousse tous les contacts (non désabonnés) dans la liste <em>Tous les clients</em>, et ceux marqués &laquo; Abonné &raquo; chez Wix dans la liste <em>Newsletter</em>.
                      </span>
                    </span>
                  </label>

                  <div className="text-[11px] text-[#B59ED1] bg-[#1B132B]/60 rounded-lg p-3 border border-white/5 leading-relaxed">
                    <strong className="text-[#E2D5F8] block mb-1">⚙️ Ce qui se passe à l&apos;import :</strong>
                    • Les emails déjà présents sont <strong>enrichis</strong> (jamais écrasés)<br />
                    • Les nouveaux clients sont créés <strong>sans mot de passe</strong> → ils devront utiliser « Mot de passe oublié »<br />
                    • Date de naissance, téléphone, adresse et labels Wix sont récupérés
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={handleWixImport}
                    disabled={!wixFile || wixImporting}
                    className="bg-[#D926A9] hover:bg-[#B5208E] text-white flex-1"
                    data-testid="wix-import-submit"
                  >
                    {wixImporting ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Import en cours…</>) : (<><Upload className="w-4 h-4 mr-2" /> Lancer l&apos;import</>)}
                  </Button>
                  <Button onClick={() => setWixImportOpen(false)} disabled={wixImporting} variant="outline" className="border-white/10">
                    Annuler
                  </Button>
                </div>

                <div className="mt-5 pt-5 border-t border-white/5">
                  <p className="text-xs text-[#B59ED1] mb-2">
                    <strong className="text-[#E2D5F8]">🔧 Maintenance one-shot</strong> — si les noms des contacts importés ne s&apos;affichent pas correctement (prénom + nom dans la même colonne) :
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/admin/contacts/fix-wix-names`, {}, { withCredentials: true });
                        toast.success(`Réparation terminée : ${res.data.fixed} contacts corrigés, ${res.data.skipped} déjà OK`);
                        const listRes = await axios.get(`${API}/clients`, { withCredentials: true });
                        setClients(listRes.data || []);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Réparation échouée");
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 hover:text-yellow-300 w-full text-xs"
                    data-testid="wix-fix-names-btn"
                  >
                    Réparer les noms des contacts Wix importés
                  </Button>
                </div>
              </>
            ) : (
              // REPORT
              <div className="space-y-4">
                <div className="bg-[#1B132B] rounded-lg p-5 border border-[#D926A9]/20">
                  <p className="text-[#D926A9] text-xs uppercase tracking-wider mb-3">Rapport d&apos;import</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-[#B59ED1]">Lignes traitées :</span> <strong className="text-white">{wixReport.total_rows}</strong></div>
                    <div><span className="text-[#B59ED1]">Nouveaux clients :</span> <strong className="text-green-400">{wixReport.created}</strong></div>
                    <div><span className="text-[#B59ED1]">Clients enrichis :</span> <strong className="text-[#00E5FF]">{wixReport.updated}</strong></div>
                    <div><span className="text-[#B59ED1]">Inchangés :</span> <strong className="text-[#B59ED1]">{wixReport.skipped_duplicates}</strong></div>
                    <div><span className="text-[#B59ED1]">Sans email :</span> <strong className="text-yellow-400">{wixReport.skipped_no_email}</strong></div>
                    <div><span className="text-[#B59ED1]">Erreurs :</span> <strong className={wixReport.errors?.length ? "text-red-400" : "text-[#B59ED1]"}>{wixReport.errors?.length || 0}</strong></div>
                  </div>
                  {wixReport.brevo && (
                    <div className="mt-4 pt-4 border-t border-white/5 text-sm">
                      <p className="text-[#B59ED1] text-xs uppercase tracking-wider mb-2">Brevo</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><span className="text-[#B59ED1]">Liste « Tous les clients » :</span> <strong className="text-white">{wixReport.brevo.all_clients_synced}</strong></div>
                        <div><span className="text-[#B59ED1]">Liste Newsletter :</span> <strong className="text-white">{wixReport.brevo.newsletter_synced}</strong></div>
                        {wixReport.brevo.failed > 0 && (
                          <div className="col-span-2"><span className="text-[#B59ED1]">Échecs Brevo :</span> <strong className="text-red-400">{wixReport.brevo.failed}</strong></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {wixReport.errors?.length > 0 && (
                  <details className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs">
                    <summary className="cursor-pointer text-red-400 font-medium">Voir les {wixReport.errors.length} erreur(s)</summary>
                    <ul className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                      {wixReport.errors.slice(0, 20).map((e, i) => (
                        <li key={`${e.row}-${e.email || i}`} className="text-red-300">Ligne {e.row} ({e.email || '—'}) : {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setWixReport(null); setWixFile(null); }}
                    variant="outline"
                    className="border-white/10 flex-1"
                  >
                    Nouvel import
                  </Button>
                  <Button
                    onClick={() => { setWixImportOpen(false); setWixReport(null); setWixFile(null); }}
                    className="bg-[#D926A9] hover:bg-[#B5208E] text-white flex-1"
                    data-testid="wix-import-done"
                  >
                    Terminer
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Profil & Sécurité (tous les utilisateurs connectés, admins inclus) */}
      {user && (
        <div className="max-w-5xl mx-auto px-6 py-8" data-testid="dashboard-profile-block">
          <h2 className="font-serif text-2xl mb-4 text-white">Mon profil &amp; sécurité</h2>
          <ProfileSecuritySection />
        </div>
      )}
    </main>
  );
}

/**
 * Section "Mes Retraites & Séjours" — visible dans le dashboard client uniquement.
 * Liste les réservations de retraites de l'utilisateur connecté avec :
 * - Statut de la résa (confirmé / à régler / payé / annulé)
 * - Bouton "Payer le solde" si status = balance_due
 * - Liens vers les reçus Stripe (acompte + solde)
 */
function MyRetreatsSection() {
  const navigate = useNavigate();
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    axios.get(`${API}/me/retreats`, { withCredentials: true })
      .then(r => setBookings(r.data.bookings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const payBalance = async (booking_id) => {
    try {
      const r = await axios.post(`${API}/me/retreats/${booking_id}/pay-balance`, {}, { withCredentials: true });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return null;
  if (!bookings.length) return null;

  return (
    <Card className="glass-card border-[#F5D8A2]/30 mb-6" data-testid="my-retreats-section">
      <CardHeader>
        <CardTitle className="font-serif text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#F5D8A2]" />
          Mes Séjours & Retraites ({bookings.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {bookings.map(b => {
          const r = b.retreat || {};
          const canPayBalance = b.status === "balance_due" || (b.status === "confirmed" && b.balance_amount > 0);
          const goToRetreat = () => { if (r.slug) navigate(`/retraites/${r.slug}`); };
          return (
            <div
              key={b.booking_id}
              className={`border border-white/10 rounded-lg p-4 transition-colors ${r.slug ? "cursor-pointer hover:border-[#F5D8A2]/50 hover:bg-white/5" : ""}`}
              data-testid={`my-retreat-${b.booking_id}`}
              onClick={goToRetreat}
              role={r.slug ? "link" : undefined}
              tabIndex={r.slug ? 0 : undefined}
              onKeyDown={(e) => { if (r.slug && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); goToRetreat(); } }}
            >
              <div className="flex justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {r.start_date} → {r.end_date}{r.location_name && ` · ${r.location_name}`}
                  </div>
                  <div className="text-xs text-white/60 mt-2">
                    {b.booking_type === "day" ? `Formule Day × ${b.day_pax}` : `${b.room_name || "Chambre"} — ${b.occupancy === "double" ? "Double" : "Simple"}`}
                    {b.companion && ` · avec ${b.companion.first_name} ${b.companion.last_name}`}
                  </div>
                  <div className="text-xs text-white/70 mt-2">
                    Payé : <strong>{(b.amount_paid || 0).toFixed(2)}€</strong> / {(b.total_amount || 0).toFixed(2)}€
                  </div>
                  <div className="flex gap-3 mt-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                    {b.deposit_receipt_url && <a href={b.deposit_receipt_url} target="_blank" rel="noopener noreferrer" className="text-[#F5D8A2] hover:underline">Reçu acompte</a>}
                    {b.balance_receipt_url && b.balance_receipt_url !== b.deposit_receipt_url && <a href={b.balance_receipt_url} target="_blank" rel="noopener noreferrer" className="text-[#F5D8A2] hover:underline">Reçu solde</a>}
                    {r.slug && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goToRetreat(); }}
                        className="text-[#F5D8A2] hover:underline"
                        data-testid={`view-retreat-${b.booking_id}`}
                      >
                        Voir le programme →
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Badge className={`${b.status === "paid" ? "bg-green-500/20 text-green-300" : b.status === "cancelled" ? "bg-red-500/20 text-red-300" : b.status === "balance_due" ? "bg-orange-500/20 text-orange-300" : "bg-blue-500/20 text-blue-300"} border-white/20`}>
                    {b.status === "paid" ? "Payé" : b.status === "cancelled" ? "Annulé" : b.status === "balance_due" ? "Solde à régler" : b.status === "confirmed" ? "Confirmé (acompte)" : b.status}
                  </Badge>
                  {canPayBalance && (
                    <div className="mt-2">
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); payBalance(b.booking_id); }} className="bg-[#F5D8A2] text-black hover:bg-[#F5D8A2]/90" data-testid={`pay-balance-${b.booking_id}`}>
                        Payer solde {(b.balance_amount || 0).toFixed(2)}€
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
